/**
 * ModuleTemplateEditor.tsx — edytor struktury szablonu (Krok 2, strona 3/3).
 *
 * Hierarchia: szablon → encje → (pola | widoki). Lewy panel: drzewo encji
 * (przeciąganie kolejności). Prawy panel: metadane wybranej encji + lista pól
 * (modal dodaj/edytuj, przeciąganie kolejności) + lista widoków (modal z konfiguracją
 * kolumn dla widoku typu „lista").
 *
 * Zapis całej struktury jednym strzałem (replace-all):
 *   - GET /admin/module-templates/:slug            → pełna definicja
 *   - PUT /admin/module-templates/:slug/structure  → { entities } (kasuje starą, wstawia nową)
 *
 * v1 „lekki edytor": własna zewnętrzna warstwa (encje) + reużycie niskopoziomowych
 * prymitywów drag-drop (DragHandle + dnd-kit). Bogata paleta z kreatora modułów
 * systemowych — przyszła rozbudowa (inny kształt danych: layouty formularzy).
 *
 * Dostęp gated permission `module_builder.designer` (backend 403 → load error).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  type AdminGlobalDocumentType,
  DOCUMENT_KARTOTEKI,
  type DocumentClass,
  emptyListConfig,
  type ListConfig,
  type MenuItem,
  MODULE_TEMPLATE_DDL_STRATEGIES,
  MODULE_TEMPLATE_FIELD_TYPES,
  MODULE_TEMPLATE_MAX_ENTITIES,
  MODULE_TEMPLATE_MAX_FIELDS_PER_ENTITY,
  MODULE_TEMPLATE_MAX_VIEWS_PER_ENTITY,
  MODULE_TEMPLATE_NAV_MODES,
  MODULE_TEMPLATE_SLUG_PATTERN,
  MODULE_TEMPLATE_VIEW_TYPES,
  type ModuleTemplateAuthor,
  type ModuleTemplateDdlStrategy,
  type ModuleTemplateDefinition,
  type ModuleTemplateDraftMenu,
  type ModuleTemplateEntityDefinition,
  type ModuleTemplateFieldDefinition,
  type ModuleTemplateFieldOption,
  type ModuleTemplateFieldType,
  type ModuleTemplateFormConfig,
  type ModuleTemplateNavMode,
  type ModuleTemplatePublishState,
  type ModuleTemplateViewDefinition,
  type ModuleTemplateViewType,
  templateListConfigFromLegacy,
  type TemplateValidationIssue,
  validateModuleTemplateStructure,
} from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import {
  ArrowLeft,
  CheckCircle,
  Eye,
  Lock,
  Paintbrush,
  PanelLeftClose,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DictionaryParamBindingsEditor } from '@/components/dictionaries/DictionaryParamBindingsEditor';
import { GlobalDictionaryPicker } from '@/components/dictionaries/GlobalDictionaryPicker';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { KartotekaBadge } from '@/components/KartotekaBadge';
import { CreateKartotekaInline } from '@/components/kartoteki/CreateKartotekaInline';
import { KartotekaFieldsManager } from '@/components/kartoteki/KartotekaFieldsManager';
import { KartotekaFormViewsPanel } from '@/components/kartoteki/KartotekaFormViewsPanel';
import { Layout } from '@/components/Layout';
import { CollapsedColumnRail, ColumnResizer } from '@/components/menu/ColumnControls';
import { MenuBuilderEmbedded } from '@/components/menu/MenuBuilderEmbedded';
import { Modal } from '@/components/Modal';
import { DragHandle } from '@/components/modules/DragHandle';
import { EntityViewPreview } from '@/components/preview/EntityViewPreview';
import { Skeleton } from '@/components/Skeleton';
import { Tabs } from '@/components/Tabs';
import { Textarea } from '@/components/Textarea';
import { useAuth } from '@/contexts/AuthContext';
import { ReferenceListContext } from '@/contexts/ReferenceListContext';
import { useTabsOptional } from '@/contexts/TabContext';
import { useToast } from '@/contexts/ToastContext';
import {
  assertNoParentFieldBindings,
  type DictionaryParamBindingsMap,
} from '@/hooks/useDictionary';
import { api } from '@/lib/api';
import { useDictionarySources, useGlobalDictionaries } from '@/lib/global-dictionaries/api';
import { kartotekaKindFromDdlStrategy } from '@/lib/kartoteka-kind';
import {
  type KartotekaBuilderField,
  useKartotekaDefinition,
  useKartotekaLibraryViews,
} from '@/lib/kartoteki/kartoteka-definition';
import {
  createDraftMenu,
  deleteDraftMenu,
  extractTemplateApiErrorCode,
  patchDraftMenu,
  publishKartotekaLayout,
  publishKartotekaList,
  putKartotekaLayoutOverride,
  selectDraftMenu,
} from '@/lib/module-templates/template-layouts';

import { SystemModuleBuilderPage } from './system-modules/SystemModuleBuilderPage';
import { SystemModuleListBuilderPage } from './system-modules/SystemModuleListBuilderPage';
import { InstallTemplateModal } from './InstallTemplateModal';
import { ReleaseTemplateModal } from './ReleaseTemplateModal';
import { TemplateInstallationsModal } from './TemplateInstallationsModal';
import { VersionHistoryModal } from './VersionHistoryModal';

// ---------------------------------------------------------------------------
// Helpery
// ---------------------------------------------------------------------------

/**
 * 7 klas dokumentów (kanonicznie `DocumentClass` z shared) dla strategii `document_class`.
 * Wartość musi być kanoniczną klasą (param `?class=` typów globalnych + pole `documentClass`),
 * a nie slugiem modułu dokumentowego z GET /admin/kartoteki (tam pluralizowany).
 */
const DOCUMENT_CLASS_VALUES: readonly DocumentClass[] = [
  'purchase_invoice',
  'sale_invoice',
  'contract',
  'letter',
  'commercial',
  'internal',
  'specjalne',
];

/** Slugifikacja nazwy → `[a-z][a-z0-9_]*` (usuwa diakrytyki — slug leci do nazwy tabeli). */
function slugify(input: string): string {
  const ascii = input.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/ł/g, 'l');
  const cleaned = ascii.replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '');
  const trimmed = cleaned.replace(/^[^a-z]+/, '');
  return trimmed;
}

function isValidSlug(slug: string): boolean {
  return MODULE_TEMPLATE_SLUG_PATTERN.test(slug);
}

function extractErrorCode(err: unknown): string | undefined {
  const ax = err as AxiosError<{ error?: string }>;
  return ax.response?.data?.error;
}

/** Przepisuje sortOrder = indeks na wszystkich poziomach przed zapisem. */
function normalizeStructure(
  entities: ModuleTemplateEntityDefinition[],
): ModuleTemplateEntityDefinition[] {
  return entities.map((entity, ei) => ({
    ...entity,
    sortOrder: ei,
    fields: entity.fields.map((field, fi) => ({ ...field, sortOrder: fi })),
    views: entity.views.map((view, vi) => ({
      ...view,
      sortOrder: vi,
      // Pełny ListConfig (ADR-0050): porządkujemy `order` kolumn (sort + renumeracja).
      listConfig: view.listConfig
        ? {
            ...view.listConfig,
            columns: [...view.listConfig.columns]
              .sort((a, b) => a.order - b.order)
              .map((col, ci) => ({ ...col, order: ci })),
          }
        : view.listConfig,
    })),
  }));
}

// ---------------------------------------------------------------------------
// Linia autorstwa — kto utworzył i kto ostatnio edytował szablon (z datami)
// ---------------------------------------------------------------------------

function TemplateAuthorLine({ author }: { author?: ModuleTemplateAuthor }) {
  const { t, i18n } = useTranslation();
  if (!author) return null;
  const { createdByName, createdAt, updatedByName, updatedAt } = author;
  if (!createdByName && !updatedByName) return null;
  const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(i18n.language) : '');
  const parts: string[] = [];
  if (createdByName) {
    parts.push(
      t('moduleTemplateEditor.author.created', { name: createdByName, date: fmt(createdAt) }),
    );
  }
  if (updatedByName) {
    parts.push(
      t('moduleTemplateEditor.author.edited', { name: updatedByName, date: fmt(updatedAt) }),
    );
  }
  return (
    <div className="mt-0.5 text-xs text-gray-400 dark:text-gray-500" data-testid="template-author">
      {parts.join(' · ')}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Plakietka stanu publikacji — trwała informacja „czy klienci mają bieżącą strukturę"
// ---------------------------------------------------------------------------

function PublishStatusBadge({
  publishState,
  dirty,
}: {
  publishState?: ModuleTemplatePublishState;
  dirty: boolean;
}) {
  const { t, i18n } = useTranslation();
  if (!publishState) return null;

  const { latestPublishedVersion, latestPublishedAt, currentSnapshotPublished } = publishState;
  const publishedAtLabel = latestPublishedAt
    ? new Date(latestPublishedAt).toLocaleDateString(i18n.language)
    : null;

  // Niewypublikowane zmiany = są zapisane edycje po ostatniej publikacji LUB niezapisany stan
  // w edytorze (dirty). „Klienci mają bieżącą" tylko gdy snapshot opublikowany i brak dirty.
  const hasUnpublished = dirty || !currentSnapshotPublished;

  if (latestPublishedVersion === null) {
    return (
      <Badge color="amber" title={t('moduleTemplateEditor.publishStatus.draftHint')}>
        {t('moduleTemplateEditor.publishStatus.draft')}
      </Badge>
    );
  }

  if (hasUnpublished) {
    return (
      <Badge
        color="amber"
        title={t('moduleTemplateEditor.publishStatus.unpublishedHint', {
          version: latestPublishedVersion,
        })}
      >
        {t('moduleTemplateEditor.publishStatus.unpublished', { version: latestPublishedVersion })}
      </Badge>
    );
  }

  return (
    <Badge
      color="green"
      title={
        publishedAtLabel
          ? t('moduleTemplateEditor.publishStatus.publishedHint', { date: publishedAtLabel })
          : undefined
      }
    >
      {t('moduleTemplateEditor.publishStatus.published', { version: latestPublishedVersion })}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Strona — edytor szablonu
// ---------------------------------------------------------------------------

export function ModuleTemplateEditor() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { slug = '' } = useParams<{ slug: string }>();
  const { hasPermission } = useAuth();
  // Hub Krok 5 — rozdział person: projektant edytuje strukturę/layouty, opiekun wypuszcza
  // i serwisuje klientów. Super-admin ma oba. Konto bez `designer` widzi edytor read-only.
  const canDesign = hasPermission('module_builder.designer');
  const canService = hasPermission('module_builder.support');

  const notifySuccess = (msg: string) => toast.show({ variant: 'success', description: msg });
  const notifyError = (msg: string) => toast.show({ variant: 'error', description: msg });

  const [entities, setEntities] = useState<ModuleTemplateEntityDefinition[]>([]);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  // Prawy panel: szczegół rodzaju danych (false) albo menu modułu (true) — wybierane z lewego panelu.
  const [menuPaneActive, setMenuPaneActive] = useState(false);
  // Menu modułu: 'default' = WYBRANE menu (modules.default_menu — jedzie do klientów po
  // publikacji), inaczej id menu ROBOCZEGO (module_menus — szkic ITE w bazie). „Wybierz"
  // zamienia robocze z wybranym (decyzja Eugenii 2026-10-01: jedzie jedno wybrane).
  const [activeMenuId, setActiveMenuId] = useState<string>('default');
  // Niezapisane zmiany w otwartym edytorze menu — blokują „Opublikuj"/„Włącz u klienta"
  // (publikacja wysyła tylko zapisane menu) i „Wybierz" dla tego menu.
  const [menuDirty, setMenuDirty] = useState(false);
  const [pendingSelectMenu, setPendingSelectMenu] = useState<{ id: string; name: string } | null>(
    null,
  );
  // Prawy panel: inline panel tworzenia nowej kartoteki (mutualnie wykluczający się z menuPane i entityDetail).
  const [createPanelOpen, setCreatePanelOpen] = useState(false);
  // Kolumna „Kartoteki / Menu modułu" — zwijalna + poszerzalna (wzorzec z Kreatora menu).
  const [entityTreeOpen, setEntityTreeOpen] = useState(true);
  const [entityTreeWidth, setEntityTreeWidth] = useState<number>(() => {
    if (typeof window === 'undefined') return 288;
    const n = Number(window.localStorage.getItem('moduleTemplateEditor.entityTreeWidth'));
    return Number.isFinite(n) && n > 0 ? Math.min(520, Math.max(240, n)) : 288;
  });
  useEffect(() => {
    window.localStorage.setItem('moduleTemplateEditor.entityTreeWidth', String(entityTreeWidth));
  }, [entityTreeWidth]);
  const [dirty, setDirty] = useState(false);
  const [installOpen, setInstallOpen] = useState(false);
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [installationsOpen, setInstallationsOpen] = useState(false);

  const detailQuery = useQuery<ModuleTemplateDefinition>({
    queryKey: ['admin', 'module-templates', slug],
    queryFn: async () => (await api.get(`/admin/module-templates/${slug}`)).data,
    retry: false,
    enabled: Boolean(slug),
  });

  // Wczytanie struktury do edytowalnego stanu lokalnego (po fetchu). Kluczem są ENCJE, nie cała
  // odpowiedź — operacje na menu roboczych podmieniają w pamięci podręcznej tylko listę menu
  // (ta sama referencja encji), więc nie kasują niezapisanych zmian w strukturze.
  const loadedEntities = detailQuery.data?.entities;
  useEffect(() => {
    if (loadedEntities) {
      setEntities(loadedEntities);
      setSelectedSlug(loadedEntities[0]?.slug ?? null);
      setDirty(false);
    }
  }, [loadedEntities]);

  const detailKey = ['admin', 'module-templates', slug];
  // Podmiana samej listy menu roboczych w pamięci podręcznej (bez ponownego pobrania encji).
  const patchCachedDrafts = (
    update: (drafts: ModuleTemplateDraftMenu[]) => ModuleTemplateDraftMenu[],
  ) =>
    queryClient.setQueryData<ModuleTemplateDefinition>(detailKey, (old) =>
      old ? { ...old, draftMenus: update(old.draftMenus ?? []) } : old,
    );

  const menuErrorMessage = (err: unknown): string => {
    const code = extractTemplateApiErrorCode(err);
    if (code === 'MENU_NAME_TAKEN') return t('moduleTemplateEditor.menuPane.duplicateMenuName');
    if (code === 'MENU_LIMIT_REACHED') return t('moduleTemplateEditor.menuPane.limitReached');
    // Równoległa praca (inna karta / inny projektant) — menu zmienione albo już go nie ma.
    if (code === 'MENU_CHANGED') return t('moduleTemplateEditor.menuPane.menuChanged');
    if (code === 'MENU_NOT_FOUND') return t('moduleTemplateEditor.menuPane.menuGone');
    if (code === 'VIEW_REF_NOT_FOUND') return t('moduleTemplateEditor.menuPane.selectDanglingView');
    if (code === 'VERSION_CONFLICT') return t('admin.templateMenuBuilder.errors.versionConflict');
    if (
      code === 'INVALID_MENU' ||
      code === 'UNSUPPORTED_MENU_ITEM_TYPE' ||
      code === 'VIEW_SEMANTIC_ERROR' ||
      code === 'MENU_TOO_LARGE'
    ) {
      return t('moduleTemplateEditor.menuPane.selectInvalid');
    }
    return t('moduleTemplateEditor.menuPane.actionFailed');
  };

  const handleAddMenu = async (name: string) => {
    try {
      const created = await createDraftMenu(slug, name);
      patchCachedDrafts((drafts) => [...drafts, created]);
      setActiveMenuId(created.id);
      setMenuPaneActive(true);
      setCreatePanelOpen(false);
    } catch (err) {
      notifyError(menuErrorMessage(err));
    }
  };

  const handleRenameMenu = async (menuId: string, name: string) => {
    try {
      const saved = await patchDraftMenu(slug, menuId, { name });
      patchCachedDrafts((drafts) => drafts.map((m) => (m.id === menuId ? saved : m)));
    } catch (err) {
      notifyError(menuErrorMessage(err));
    }
  };

  const handleDeleteMenu = async (menuId: string) => {
    try {
      await deleteDraftMenu(slug, menuId);
      patchCachedDrafts((drafts) => drafts.filter((m) => m.id !== menuId));
      if (activeMenuId === menuId) setActiveMenuId('default');
    } catch (err) {
      notifyError(menuErrorMessage(err));
    }
  };

  const selectMenuMutation = useMutation({
    mutationFn: async (menu: { id: string; name: string }) => selectDraftMenu(slug, menu.id),
    onSuccess: (_res, menu) => {
      notifySuccess(t('moduleTemplateEditor.menuPane.selectedToast', { name: menu.name }));
      setActiveMenuId('default');
      // Zamiana bumpuje wersję i robi snapshot — pełne odświeżenie (status publikacji też).
      void queryClient.invalidateQueries({ queryKey: detailKey });
    },
    onError: (err) => notifyError(menuErrorMessage(err)),
  });

  // Dlaczego „Wybierz" jest teraz niedostępne (null = dostępne).
  const selectBlockedReason = (_menuId: string): string | null => {
    if (dirty) return t('moduleTemplateEditor.menuPane.selectBlockedStructure');
    // Dowolne otwarte menu z niezapisanymi zmianami — po zamianie edytor się przełącza
    // i te zmiany przepadłyby po cichu.
    if (menuDirty) return t('moduleTemplateEditor.menuPane.selectBlockedMenu');
    return null;
  };

  // Etykieta karty = „Szablon modułu — <nazwa>" (odróżnia kilka otwartych edytorów).
  // Trafiamy w kartę PO `basePath` (nie w aktywną) — panele są keep-alive, więc
  // aktywna może być inna. Poza powłoką kart (testy) `tabCtx` jest null → no-op.
  const tabCtx = useTabsOptional();
  useEffect(() => {
    if (!tabCtx || !detailQuery.data) return;
    const basePath = `/module-templates/${slug}`;
    const myTab = tabCtx.tabs.find((tt) => tt.basePath === basePath);
    const label = `Szablon modułu — ${detailQuery.data.name}`;
    if (myTab && myTab.label !== label) tabCtx.updateTabLabel(myTab.id, label);
  }, [tabCtx, detailQuery.data, slug]);

  const saveMutation = useMutation({
    mutationFn: async (payload: ModuleTemplateEntityDefinition[]) =>
      (await api.put(`/admin/module-templates/${slug}/structure`, { entities: payload })).data,
    onSuccess: () => {
      notifySuccess(t('moduleTemplateEditor.toast.saved'));
      setDirty(false);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'module-templates'] });
      // Odśwież detal — zapis bumpuje wersję techniczną i tworzy NIEopublikowany snapshot,
      // więc plakietka statusu musi przeskoczyć na „niewypublikowane zmiany".
      void queryClient.invalidateQueries({ queryKey: ['admin', 'module-templates', slug] });
    },
    onError: (err) => {
      const code = extractErrorCode(err);
      notifyError(
        code === 'VALIDATION_FAILED'
          ? t('moduleTemplateEditor.toast.validationError')
          : code === 'ENTITY_PUBLISHED_CANNOT_DELETE'
            ? t('moduleTemplateEditor.errors.entityPublishedCannotDelete')
            : code === 'FIELD_PUBLISHED_CANNOT_DELETE'
              ? t('moduleTemplateEditor.errors.fieldPublishedCannotDelete')
              : code === 'FIELD_PUBLISHED_CANNOT_CHANGE_TYPE'
                ? t('moduleTemplateEditor.errors.fieldPublishedCannotChangeType')
                : code === 'FIELD_PUBLISHED_CANNOT_MODIFY'
                  ? t('moduleTemplateEditor.errors.fieldPublishedCannotModify')
                  : t('moduleTemplateEditor.toast.saveError'),
      );
    },
  });

  const selectedEntity = useMemo(
    () => entities.find((e) => e.slug === selectedSlug) ?? null,
    [entities, selectedSlug],
  );

  // Walidacja struktury (czysta funkcja z @saas/shared — lustro reguł backendu).
  const issues = useMemo(() => validateModuleTemplateStructure(entities), [entities]);
  const errorEntitySlugs = useMemo(
    () =>
      new Set(
        issues
          .filter((i) => i.severity === 'error' && i.entitySlug)
          .map((i) => i.entitySlug as string),
      ),
    [issues],
  );
  const hasErrors = issues.some((i) => i.severity === 'error');

  // --- mutatory stanu (immutable) ---
  const mutateEntities = (next: ModuleTemplateEntityDefinition[]) => {
    setEntities(next);
    setDirty(true);
  };

  const upsertEntity = (entity: ModuleTemplateEntityDefinition, originalSlug?: string) => {
    if (originalSlug) {
      mutateEntities(entities.map((e) => (e.slug === originalSlug ? entity : e)));
    } else {
      mutateEntities([...entities, entity]);
    }
    setSelectedSlug(entity.slug);
  };

  // Dodanie wielu encji naraz (kilka gotowych kartotek z checkboxów) jednym setState —
  // jedno wywołanie z aktualnym `entities` (wielokrotny upsertEntity gubiłby wszystkie poza ostatnią).
  const upsertEntities = (newEntities: ModuleTemplateEntityDefinition[]) => {
    if (newEntities.length === 0) return;
    mutateEntities([...entities, ...newEntities]);
    setSelectedSlug(newEntities[0]!.slug);
    // Zamknij inline create panel — przejście do EntityDetail nowej encji
    setCreatePanelOpen(false);
  };

  const removeEntity = (entitySlug: string) => {
    const next = entities.filter((e) => e.slug !== entitySlug);
    mutateEntities(next);
    if (selectedSlug === entitySlug) setSelectedSlug(next[0]?.slug ?? null);
  };

  const reorderEntities = (fromSlug: string, toSlug: string) => {
    const from = entities.findIndex((e) => e.slug === fromSlug);
    const to = entities.findIndex((e) => e.slug === toSlug);
    if (from < 0 || to < 0) return;
    mutateEntities(arrayMove(entities, from, to));
  };

  const updateSelectedEntity = (patch: Partial<ModuleTemplateEntityDefinition>) => {
    if (!selectedEntity) return;
    mutateEntities(entities.map((e) => (e.slug === selectedEntity.slug ? { ...e, ...patch } : e)));
  };

  /**
   * Synchronizacja configu widoku po natychmiastowym PATCH z buildera (krok 4 — clobber guard).
   * Aktualizuje lokalny stan edytora BEZ setDirty=true — config jest już na serwerze,
   * więc kolejny PUT structure nie nadpisze go starą wartością.
   */
  const syncViewConfig = (
    entitySlug: string,
    viewSlug: string,
    config: { formConfig?: ModuleTemplateFormConfig; listConfig?: ListConfig },
  ) => {
    setEntities((prev) =>
      prev.map((e) =>
        e.slug === entitySlug
          ? {
              ...e,
              views: e.views.map((v) =>
                v.slug === viewSlug
                  ? {
                      ...v,
                      ...(config.formConfig !== undefined ? { formConfig: config.formConfig } : {}),
                      ...(config.listConfig !== undefined ? { listConfig: config.listConfig } : {}),
                    }
                  : v,
              ),
            }
          : e,
      ),
    );
    // NIE wołamy setDirty(true) — config jest już na serwerze po PATCH
  };

  /**
   * Etap 2 kartoteki — synchronizuje `entity.kartotekaLayoutOverride` w lokalnym stanie
   * edytora po udanym PUT .../kartoteka-layout. BEZ setDirty — override już na serwerze.
   */
  const syncKartotekaLayout = (entitySlug: string, layoutConfig: Record<string, unknown>) => {
    setEntities((prev) =>
      prev.map((e) =>
        e.slug === entitySlug ? { ...e, kartotekaLayoutOverride: layoutConfig } : e,
      ),
    );
    // NIE wołamy setDirty(true) — override jest już na serwerze po PUT
  };

  const handleSave = () => {
    saveMutation.mutate(normalizeStructure(entities));
  };

  if (detailQuery.isError) {
    return (
      <Layout>
        <section className="mx-auto max-w-7xl px-6 py-8">
          <div className="rounded border border-red-300 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300">
            {t('moduleTemplateEditor.loadError')}
          </div>
        </section>
      </Layout>
    );
  }

  if (detailQuery.isLoading || !detailQuery.data) {
    return (
      <Layout>
        <section className="mx-auto max-w-7xl px-6 py-8">
          <Skeleton className="h-64 w-full" />
        </section>
      </Layout>
    );
  }

  const tpl = detailQuery.data;

  return (
    <Layout>
      {/* Kreator menu: edytor ma wysokość okna (minus chrome panelu, jak kreator menu ITE),
          a kreator wypełnia resztę — paleta i płótno przewijają się osobno, strona nie. */}
      <section
        className={menuPaneActive ? 'flex h-[calc(100vh-6.5rem)] flex-col px-6 py-8' : 'px-6 py-8'}
      >
        <div className="mb-6 flex shrink-0 flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/module-templates')}
              className="rounded p-1.5 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
              aria-label={t('common.back')}
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-semibold">{tpl.name}</h1>
              <div className="mt-0.5 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <code>{tpl.slug}</code>
                <PublishStatusBadge publishState={tpl.publishState} dirty={dirty} />
              </div>
              <TemplateAuthorLine author={tpl.author} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => setInstallationsOpen(true)}>
              {t('moduleTemplateEditor.installations.button')}
            </Button>
            <Button variant="secondary" onClick={() => setHistoryOpen(true)}>
              {t('moduleTemplateEditor.versions.button')}
            </Button>
            {/* Wypuść i Instaluj — akcje serwisowe opiekuna (hub Krok 5). */}
            {canService && (
              <>
                {/* Niezapisane menu też blokuje — publikacja/instalacja wysyła tylko to, co
                    zapisane w bazie, więc bez blokady zmiany z ekranu przepadłyby po cichu. */}
                <Button
                  variant="secondary"
                  onClick={() => setReleaseOpen(true)}
                  disabled={dirty || menuDirty || entities.length === 0}
                  title={
                    dirty
                      ? t('moduleTemplateEditor.release.dirtyHint')
                      : menuDirty
                        ? t('moduleTemplateEditor.release.menuDirtyHint')
                        : entities.length === 0
                          ? t('moduleTemplateEditor.release.emptyHint')
                          : undefined
                  }
                  data-testid="template-release-button"
                >
                  {t('moduleTemplateEditor.release.button')}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setInstallOpen(true)}
                  disabled={dirty || menuDirty || entities.length === 0}
                  title={
                    dirty
                      ? t('moduleTemplateEditor.install.dirtyHint')
                      : menuDirty
                        ? t('moduleTemplateEditor.install.menuDirtyHint')
                        : entities.length === 0
                          ? t('moduleTemplateEditor.install.emptyHint')
                          : undefined
                  }
                  data-testid="template-install-button"
                >
                  {t('moduleTemplateEditor.install.button')}
                </Button>
              </>
            )}
            {/* Zapis struktury — tylko projektant (hub Krok 5). */}
            {canDesign && (
              <Button
                onClick={handleSave}
                disabled={!dirty || saveMutation.isPending || hasErrors}
                title={hasErrors ? t('moduleTemplateEditor.validation.saveBlocked') : undefined}
              >
                {t('moduleTemplateEditor.save')}
              </Button>
            )}
          </div>
        </div>

        {/* Konto bez roli projektanta (np. sam opiekun) — edytor czytany, edycja zablokowana. */}
        {!canDesign && (
          <div
            className="mb-4 shrink-0 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm text-blue-800 dark:border-blue-900/40 dark:bg-blue-950/30 dark:text-blue-300"
            data-testid="readonly-notice"
          >
            {t('moduleTemplateEditor.readOnlyNotice')}
          </div>
        )}

        <ValidationPanel
          issues={issues}
          onSelectEntity={(s) => {
            setSelectedSlug(s);
            setMenuPaneActive(false);
            setCreatePanelOpen(false);
          }}
        />

        <div className={menuPaneActive ? 'flex min-h-[360px] flex-1' : 'flex'}>
          {entityTreeOpen ? (
            <>
              <div className="min-w-0 shrink-0 overflow-y-auto" style={{ width: entityTreeWidth }}>
                <EntityTree
                  entities={entities}
                  templateSlug={tpl.slug}
                  selectedSlug={menuPaneActive || createPanelOpen ? null : selectedSlug}
                  errorEntitySlugs={errorEntitySlugs}
                  readOnly={!canDesign}
                  menuActive={menuPaneActive}
                  selectedMenu={tpl.defaultMenu ?? []}
                  selectedMenuName={
                    tpl.defaultMenuName ?? t('moduleTemplateEditor.menuPane.defaultMenuLabel')
                  }
                  draftMenus={tpl.draftMenus ?? []}
                  activeMenuId={activeMenuId}
                  selectBlockedReason={selectBlockedReason}
                  selectPending={selectMenuMutation.isPending}
                  onSelectDraft={(menu) => setPendingSelectMenu(menu)}
                  onSelect={(s) => {
                    setSelectedSlug(s);
                    setMenuPaneActive(false);
                    setCreatePanelOpen(false);
                  }}
                  onSelectMenu={(menuId) => {
                    setActiveMenuId(menuId);
                    setMenuPaneActive(true);
                    setCreatePanelOpen(false);
                  }}
                  onAddMenu={(name) => void handleAddMenu(name)}
                  onDeleteMenu={(menuId) => void handleDeleteMenu(menuId)}
                  onRenameMenu={(menuId, name) => void handleRenameMenu(menuId, name)}
                  onStartCreate={() => {
                    setCreatePanelOpen(true);
                    setMenuPaneActive(false);
                    setSelectedSlug(null);
                  }}
                  onReorder={reorderEntities}
                  onUpsert={upsertEntity}
                  onRemove={removeEntity}
                  onCollapse={() => setEntityTreeOpen(false)}
                />
              </div>
              <ColumnResizer
                width={entityTreeWidth}
                onWidthChange={setEntityTreeWidth}
                min={240}
                max={520}
              />
            </>
          ) : (
            <CollapsedColumnRail
              label={t('moduleTemplateEditor.entities', { defaultValue: 'Kartoteki' })}
              ariaLabel={t('moduleTemplateEditor.entities', { defaultValue: 'Kartoteki' })}
              onExpand={() => setEntityTreeOpen(true)}
              testId="module-template-entities-expand"
            />
          )}

          <div
            className={menuPaneActive ? 'flex min-w-0 flex-1 flex-col pl-6' : 'min-w-0 flex-1 pl-6'}
          >
            {menuPaneActive ? (
              activeMenuId === 'default' ||
              !(tpl.draftMenus ?? []).some((m) => m.id === activeMenuId) ? (
                <MenuBuilderEmbedded
                  // Wybrane menu (jedzie do klientów). Re-mount po zamianie — inna treść i nazwa.
                  key={`selected:${tpl.defaultMenuName ?? ''}`}
                  templateSlug={tpl.slug}
                  moduleName={tpl.name}
                  entities={entities}
                  initialMenu={tpl.defaultMenu ?? []}
                  readOnly={!canDesign}
                  onDirtyChange={setMenuDirty}
                />
              ) : (
                <MenuBuilderEmbedded
                  // Menu robocze — re-mount per id (świeża working copy), zapis do module_menus.
                  key={activeMenuId}
                  templateSlug={tpl.slug}
                  moduleName={tpl.name}
                  entities={entities}
                  initialMenu={
                    (tpl.draftMenus ?? []).find((m) => m.id === activeMenuId)?.hierarchy ?? []
                  }
                  readOnly={!canDesign}
                  draftMenuId={activeMenuId}
                  onDirtyChange={setMenuDirty}
                />
              )
            ) : createPanelOpen ? (
              <CreateKartotekaInline
                templateSlug={tpl.slug}
                existingSlugs={entities.map((e) => e.slug)}
                onSubmit={(ents) => {
                  upsertEntities(ents);
                  // upsertEntities samo ustawia createPanelOpen=false + selectedSlug
                }}
                onClose={() => setCreatePanelOpen(false)}
                readOnly={!canDesign}
              />
            ) : selectedEntity ? (
              <EntityDetail
                key={selectedEntity.slug}
                entity={selectedEntity}
                templateSlug={tpl.slug}
                structureDirty={dirty}
                readOnly={!canDesign}
                onChangeMeta={updateSelectedEntity}
                onChangeFields={(fields) => updateSelectedEntity({ fields })}
                onChangeViews={(views) => updateSelectedEntity({ views })}
                onViewConfigPersisted={(viewSlug, config) =>
                  syncViewConfig(selectedEntity.slug, viewSlug, config)
                }
                onKartotekaLayoutSaved={(entitySlug, layoutConfig) =>
                  syncKartotekaLayout(entitySlug, layoutConfig)
                }
              />
            ) : (
              <div className="flex items-center justify-center rounded-lg border border-dashed border-gray-300 p-12 text-center text-gray-500 dark:border-gray-700 dark:text-gray-400">
                {t('moduleTemplateEditor.noEntitySelected')}
              </div>
            )}
          </div>
        </div>

        <InstallTemplateModal
          open={installOpen}
          onClose={() => setInstallOpen(false)}
          templateSlug={tpl.slug}
          templateName={tpl.name}
        />

        <ReleaseTemplateModal
          open={releaseOpen}
          onClose={() => setReleaseOpen(false)}
          templateSlug={tpl.slug}
          currentClientVersion={tpl.clientVersion}
          onReleased={() => {
            void queryClient.invalidateQueries({ queryKey: ['admin', 'module-templates', slug] });
            void queryClient.invalidateQueries({ queryKey: ['admin', 'module-templates'] });
          }}
        />

        <VersionHistoryModal
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          templateSlug={tpl.slug}
        />

        <TemplateInstallationsModal
          open={installationsOpen}
          onClose={() => setInstallationsOpen(false)}
          templateSlug={tpl.slug}
        />

        {/* „Wybierz" menu robocze — zmienia, co pojedzie do klientów (po publikacji), więc
            z potwierdzeniem; nie jest destrukcyjne (poprzednie wybrane zostaje jako robocze). */}
        <ConfirmDialog
          open={pendingSelectMenu !== null}
          variant="primary"
          title={t('moduleTemplateEditor.menuPane.selectConfirmTitle', {
            name: pendingSelectMenu?.name ?? '',
          })}
          message={t('moduleTemplateEditor.menuPane.selectConfirmMessage', {
            current: tpl.defaultMenuName ?? t('moduleTemplateEditor.menuPane.defaultMenuLabel'),
          })}
          confirmLabel={t('moduleTemplateEditor.menuPane.selectMenu')}
          onConfirm={() => {
            if (pendingSelectMenu) selectMenuMutation.mutate(pendingSelectMenu);
            setPendingSelectMenu(null);
          }}
          onClose={() => setPendingSelectMenu(null)}
        />
      </section>
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Panel walidacji — błędy (blokują zapis) + ostrzeżenia (informacyjne)
// ---------------------------------------------------------------------------

function ValidationPanel({
  issues,
  onSelectEntity,
}: {
  issues: TemplateValidationIssue[];
  onSelectEntity: (slug: string) => void;
}) {
  const { t } = useTranslation();
  if (issues.length === 0) return null;

  const errorCount = issues.filter((i) => i.severity === 'error').length;
  const warningCount = issues.length - errorCount;

  return (
    <div
      className="mb-4 shrink-0 rounded-lg border border-gray-200 dark:border-gray-700"
      role="alert"
      aria-label={t('moduleTemplateEditor.validation.title')}
    >
      <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 px-4 py-2 dark:border-gray-700">
        <span className="text-sm font-semibold">{t('moduleTemplateEditor.validation.title')}</span>
        {errorCount > 0 && (
          <Badge color="red">
            {t('moduleTemplateEditor.validation.errorCount', { count: errorCount })}
          </Badge>
        )}
        {warningCount > 0 && (
          <Badge color="amber">
            {t('moduleTemplateEditor.validation.warningCount', { count: warningCount })}
          </Badge>
        )}
      </div>
      <ul className="flex flex-col divide-y divide-gray-100 dark:divide-gray-800">
        {issues.map((issue, idx) => {
          const message = t(`moduleTemplateEditor.validation.${issue.code}`, { ...issue.params });
          const location = [issue.entitySlug, issue.fieldSlug, issue.viewSlug]
            .filter(Boolean)
            .join(' › ');
          return (
            <li key={`${issue.code}-${issue.entitySlug ?? ''}-${idx}`}>
              <button
                type="button"
                onClick={() => issue.entitySlug && onSelectEntity(issue.entitySlug)}
                disabled={!issue.entitySlug}
                className="flex w-full items-start gap-2 px-4 py-2 text-left text-sm hover:bg-gray-50 disabled:cursor-default disabled:hover:bg-transparent dark:hover:bg-gray-800/50"
              >
                <Badge color={issue.severity === 'error' ? 'red' : 'amber'}>
                  {t(`moduleTemplateEditor.validation.severity.${issue.severity}`)}
                </Badge>
                <span className="min-w-0 flex-1">
                  <span>{message}</span>
                  {location && (
                    <code className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                      {location}
                    </code>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Drzewo encji (lewy panel)
// ---------------------------------------------------------------------------

function EntityTree({
  entities,
  templateSlug,
  selectedSlug,
  errorEntitySlugs,
  readOnly = false,
  menuActive,
  selectedMenu,
  selectedMenuName,
  draftMenus,
  activeMenuId,
  selectBlockedReason,
  selectPending = false,
  onSelectDraft,
  onSelect,
  onSelectMenu,
  onAddMenu,
  onDeleteMenu,
  onRenameMenu,
  onStartCreate,
  onReorder,
  onUpsert,
  onRemove,
  onCollapse,
}: {
  entities: ModuleTemplateEntityDefinition[];
  /** Kod bieżącego szablonu — wykluczamy go z pickera odwołań do kartotek Kreatora (Etap 3). */
  templateSlug: string;
  selectedSlug: string | null;
  errorEntitySlugs: Set<string>;
  /** Hub Krok 5 — konto bez roli projektanta: ukrywamy „+"/edycję/usuwanie/przeciąganie. */
  readOnly?: boolean;
  /** Czy aktywna jest pozycja „Menu modułu" (prawy panel pokazuje menu, nie encję). */
  menuActive: boolean;
  /** Hierarchia WYBRANEGO menu (jedzie do klientów) — liczba pozycji / empty-state w wierszu. */
  selectedMenu: MenuItem[];
  /** Nazwa wybranego menu (fallback „Domyślne menu"). */
  selectedMenuName: string;
  /** Menu ROBOCZE — szkice ITE zapisane w bazie, nie jadą do klientów. */
  draftMenus: ModuleTemplateDraftMenu[];
  /** Które menu jest otwarte w prawym panelu ('default' = wybrane, inaczej id roboczego). */
  activeMenuId: string;
  /** Powód zablokowania „Wybierz" dla danego menu roboczego (null = dostępne). */
  selectBlockedReason: (menuId: string) => string | null;
  /** Trwa zamiana menu — przyciski „Wybierz" nieaktywne. */
  selectPending?: boolean;
  /** „Wybierz" przy menu roboczym — rodzic pyta o potwierdzenie i zamienia menu. */
  onSelectDraft: (menu: { id: string; name: string }) => void;
  onSelect: (slug: string) => void;
  /** Klik w wiersz menu — przełącza prawy panel na to menu ('default' lub id roboczego). */
  onSelectMenu: (menuId: string) => void;
  /** „+" przy menu — tworzy menu robocze o podanej nazwie. */
  onAddMenu: (name: string) => void;
  /** Usuń menu robocze. */
  onDeleteMenu: (menuId: string) => void;
  /** Zmień nazwę menu roboczego (inline rename). */
  onRenameMenu: (menuId: string, name: string) => void;
  /** Klik „+" — otwiera inline create panel w prawej kolumnie (zarządza główny komponent). */
  onStartCreate: () => void;
  onReorder: (fromSlug: string, toSlug: string) => void;
  onUpsert: (entity: ModuleTemplateEntityDefinition, originalSlug?: string) => void;
  onRemove: (slug: string) => void;
  /** Zwinięcie kolumny — ikonka w nagłówku. Brak = bez ikonki. */
  onCollapse?: () => void;
}) {
  const { t } = useTranslation();
  const [editEntity, setEditEntity] = useState<ModuleTemplateEntityDefinition | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ModuleTemplateEntityDefinition | null>(null);
  // Modal „nowe menu" (nazwa) — lokalny stan lewego panelu.
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [newMenuName, setNewMenuName] = useState('');
  const [newMenuError, setNewMenuError] = useState<string | null>(null);

  // Czy nazwa koliduje z którymś istniejącym menu (case-insensitive, trim) — porównujemy
  // ze WSZYSTKIMI menu: roboczymi ORAZ wybranym. `excludeId` pomija jedno menu (rename nie
  // koliduje sam ze sobą). Lustro reguły serwera (409 MENU_NAME_TAKEN łapie resztę wyścigów).
  const isDuplicateMenuName = (name: string, excludeId?: string): boolean => {
    const needle = name.trim().toLowerCase();
    if (!needle) return false;
    if (needle === selectedMenuName.trim().toLowerCase()) return true;
    return draftMenus.some((m) => m.id !== excludeId && m.name.trim().toLowerCase() === needle);
  };

  // Otwarcie okna czyści błąd poprzedniej próby. Fokus na polu „Nazwa" daje `autoFocus` na
  // polu (wspólny rdzeń okien nie przejmuje fokusu od pola z `autoFocus`).
  useEffect(() => {
    if (showAddMenu) setNewMenuError(null);
  }, [showAddMenu]);
  // Potwierdzenie usunięcia nazwanego menu — §4.12 (ConfirmDialog, nie window.confirm).
  const [pendingDeleteMenu, setPendingDeleteMenu] = useState<{ id: string; name: string } | null>(
    null,
  );

  // Wspólny create dla „Utwórz" i Enter w polu nazwy (guard na pustą nazwę + duplikat).
  const submitNewMenu = () => {
    const name = newMenuName.trim();
    if (!name) return;
    if (isDuplicateMenuName(name)) {
      setNewMenuError(t('moduleTemplateEditor.menuPane.duplicateMenuName'));
      return;
    }
    onAddMenu(name);
    setShowAddMenu(false);
    setNewMenuName('');
    setNewMenuError(null);
  };

  // Inline rename nazwanego menu — które menu edytujemy + bieżąca wartość pola.
  const [renamingMenuId, setRenamingMenuId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const startRenameMenu = (id: string, currentName: string) => {
    setRenamingMenuId(id);
    setRenameValue(currentName);
  };
  const cancelRenameMenu = () => {
    setRenamingMenuId(null);
    setRenameValue('');
  };
  const commitRenameMenu = (id: string) => {
    const name = renameValue.trim();
    const current = draftMenus.find((m) => m.id === id)?.name ?? '';
    // Pusta nazwa albo bez zmian → cofnij (rewert do starej). Duplikat (kolizja z innym
    // menu, w tym domyślnym) też odrzucamy — ta sama reguła co przy tworzeniu.
    if (name && name !== current && !isDuplicateMenuName(name, id)) {
      onRenameMenu(id, name);
    }
    cancelRenameMenu();
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      onReorder(String(active.id), String(over.id));
    }
  };

  const existingSlugs = entities.map((e) => e.slug);
  const atLimit = entities.length >= MODULE_TEMPLATE_MAX_ENTITIES;

  return (
    <aside className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {t('moduleTemplateEditor.entities')}
        </h2>
        <div className="flex items-center gap-1">
          {onCollapse ? (
            <button
              type="button"
              onClick={onCollapse}
              aria-label={t('moduleTemplateEditor.hideEntities', { defaultValue: 'Zwiń kolumnę' })}
              title={t('moduleTemplateEditor.hideEntities', { defaultValue: 'Zwiń kolumnę' })}
              className="rounded-md p-1 text-text-muted transition hover:bg-bg-secondary hover:text-text-primary"
              data-testid="module-template-entities-collapse"
            >
              <PanelLeftClose className="h-4 w-4" />
            </button>
          ) : null}
          {!readOnly && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onStartCreate()}
              disabled={atLimit}
              aria-label={t('moduleTemplateEditor.addEntity')}
              title={
                atLimit
                  ? t('moduleTemplateEditor.entityLimit')
                  : t('moduleTemplateEditor.addEntity')
              }
            >
              <Plus className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {entities.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 p-4 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
          {t('moduleTemplateEditor.noEntities')}
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={existingSlugs} strategy={verticalListSortingStrategy}>
            <ul className="flex flex-col gap-1">
              {entities.map((entity) => (
                <SortableEntityRow
                  key={entity.slug}
                  entity={entity}
                  selected={entity.slug === selectedSlug}
                  hasError={errorEntitySlugs.has(entity.slug)}
                  readOnly={readOnly}
                  onSelect={() => onSelect(entity.slug)}
                  onEdit={() => setEditEntity(entity)}
                  onDelete={() => setPendingDelete(entity)}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      {/* Menu modułu — „Domyślne menu" (zapis do bazy) + dodatkowe nazwane menu (lokalne,
          robocze). „+" dodaje nazwane menu; klik wiersza przełącza prawy panel na builder. */}
      <div className="mt-2 border-t border-gray-200 pt-3 dark:border-gray-700">
        <div className="mb-1.5 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {t('moduleTemplateEditor.menuSection')}
          </h2>
          {!readOnly && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setShowAddMenu(true)}
              aria-label={t('moduleTemplateEditor.menuPane.addMenu', {
                defaultValue: 'Dodaj menu',
              })}
              title={t('moduleTemplateEditor.menuPane.addMenu', { defaultValue: 'Dodaj menu' })}
              data-testid="add-module-menu"
            >
              <Plus className="h-4 w-4" />
            </Button>
          )}
        </div>
        {/* Wybrane menu (jedzie do klientów po publikacji) zawsze na górze; pod nim menu robocze
            (szkice w bazie). „Wybierz" zamienia robocze z wybranym (z potwierdzeniem u rodzica). */}
        {(() => {
          const rowCardClass = (isSelected: boolean, isOpen: boolean) =>
            [
              'group flex items-center gap-2 rounded-lg border px-2 py-1.5',
              isSelected
                ? 'border-2 border-accent-400 bg-accent-50 dark:bg-accent-950/20'
                : isOpen
                  ? 'border-accent-400 bg-accent-50 dark:bg-accent-950/30'
                  : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600',
            ].join(' ');

          const itemCountLabel = (items: MenuItem[]) =>
            items.length === 0
              ? t('moduleTemplateEditor.menuPane.emptyHint')
              : t('moduleTemplateEditor.menuPane.itemCount', { count: countMenuItems(items) });

          const selectedRow = (
            <li
              key="default"
              className={rowCardClass(true, menuActive && activeMenuId === 'default')}
              title={t('moduleTemplateEditor.menuPane.selectedHint')}
            >
              <button
                type="button"
                onClick={() => onSelectMenu('default')}
                data-testid="select-module-menu"
                className="flex min-w-0 flex-1 items-center gap-2 text-left"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center truncate text-sm font-medium">
                    {selectedMenuName}
                    <span
                      className="ml-1.5 rounded-full bg-accent-100 px-2 py-0.5 text-[10px] font-semibold text-accent-700"
                      data-testid="module-menu-selected-badge"
                    >
                      {t('moduleTemplateEditor.menuPane.selectedBadge')}
                    </span>
                  </div>
                  <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                    {itemCountLabel(selectedMenu)}
                  </div>
                </div>
              </button>
            </li>
          );

          const draftRow = (m: ModuleTemplateDraftMenu) => {
            if (renamingMenuId === m.id) {
              // Inline rename — input w miejscu wiersza. Enter/blur = commit, Escape = anuluj.
              return (
                <li
                  key={m.id}
                  className="group flex items-center gap-2 rounded-lg border border-accent-400 px-2 py-1.5"
                >
                  <input
                    type="text"
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        commitRenameMenu(m.id);
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        cancelRenameMenu();
                      }
                    }}
                    onBlur={() => commitRenameMenu(m.id)}
                    className="min-w-0 flex-1 rounded border border-gray-300 bg-bg-primary px-1.5 py-0.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-500 dark:border-gray-600"
                    autoFocus
                    data-testid={`rename-module-menu-input-${m.id}`}
                  />
                </li>
              );
            }
            const blocked = selectBlockedReason(m.id);
            return (
              <li
                key={m.id}
                className={rowCardClass(false, menuActive && activeMenuId === m.id)}
                title={t('moduleTemplateEditor.menuPane.draftHint')}
              >
                <button
                  type="button"
                  onClick={() => onSelectMenu(m.id)}
                  data-testid={`select-module-menu-${m.id}`}
                  className="flex min-w-0 flex-1 items-center gap-2 text-left"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center truncate text-sm font-medium">
                      {m.name}
                      <span className="ml-1.5 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                        {t('moduleTemplateEditor.menuPane.draftBadge')}
                      </span>
                    </div>
                    <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                      {itemCountLabel(m.hierarchy)}
                    </div>
                  </div>
                </button>
                {!readOnly && (
                  <div className="ml-auto flex shrink-0 items-center gap-0.5">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!blocked) onSelectDraft({ id: m.id, name: m.name });
                      }}
                      disabled={blocked !== null || selectPending}
                      title={blocked ?? t('moduleTemplateEditor.menuPane.selectHint')}
                      className="flex items-center gap-1 rounded-md border border-accent-300 px-2 py-1 text-xs font-medium text-accent-600 hover:bg-accent-50 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-accent-950/20"
                      data-testid={`menu-assigned-${m.id}`}
                    >
                      <CheckCircle className="h-3.5 w-3.5" />
                      {t('moduleTemplateEditor.menuPane.selectMenu')}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        startRenameMenu(m.id, m.name);
                      }}
                      className="flex items-center rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                      title={t('moduleTemplateEditor.menuPane.editMenuLabel', {
                        defaultValue: 'Edytuj nazwę',
                      })}
                      aria-label={t('moduleTemplateEditor.menuPane.editMenuLabel', {
                        defaultValue: 'Edytuj nazwę',
                      })}
                      data-testid={`rename-module-menu-${m.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPendingDeleteMenu({ id: m.id, name: m.name });
                      }}
                      className="flex items-center rounded p-1 text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                      title={t('moduleTemplateEditor.menuPane.deleteMenuLabel', {
                        defaultValue: 'Usuń',
                      })}
                      aria-label={t('moduleTemplateEditor.menuPane.deleteMenuLabel', {
                        defaultValue: 'Usuń',
                      })}
                      data-testid={`delete-module-menu-${m.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </li>
            );
          };

          return (
            <ul className="flex flex-col gap-1">
              {selectedRow}
              {draftMenus.map(draftRow)}
            </ul>
          );
        })()}
      </div>

      {showAddMenu && (
        <Modal
          open
          onClose={() => {
            setShowAddMenu(false);
            setNewMenuName('');
            setNewMenuError(null);
          }}
          title={t('moduleTemplateEditor.menuPane.newMenuTitle', { defaultValue: 'Nowe menu' })}
          footer={
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowAddMenu(false);
                  setNewMenuName('');
                  setNewMenuError(null);
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                disabled={!newMenuName.trim()}
                onClick={submitNewMenu}
                data-testid="add-module-menu-confirm"
              >
                {t('moduleTemplateEditor.menuPane.newMenuConfirm', { defaultValue: 'Utwórz' })}
              </Button>
            </div>
          }
        >
          <Input
            autoFocus
            label={t('moduleTemplateEditor.menuPane.newMenuNameLabel', { defaultValue: 'Nazwa' })}
            value={newMenuName}
            onChange={(e) => {
              setNewMenuName(e.target.value);
              // Edycja czyści błąd duplikatu — walidacja ponawiana przy próbie zapisu.
              if (newMenuError) setNewMenuError(null);
            }}
            onKeyDown={(e) => {
              // Enter = ten sam create co przycisk „Utwórz" (guard na pustą nazwę + duplikat).
              if (e.key === 'Enter' && newMenuName.trim()) {
                e.preventDefault();
                submitNewMenu();
              }
            }}
            placeholder={t('moduleTemplateEditor.menuPane.newMenuPlaceholder', {
              defaultValue: 'np. Menu sprzedawcy',
            })}
            error={newMenuError ?? undefined}
            data-testid="add-module-menu-name"
          />
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {t('moduleTemplateEditor.menuPane.newMenuHint')}
          </p>
        </Modal>
      )}

      {editEntity && (
        <EntityFormModal
          open
          mode="edit"
          entity={editEntity}
          templateSlug={templateSlug}
          existingSlugs={existingSlugs.filter((s) => s !== editEntity.slug)}
          onClose={() => setEditEntity(null)}
          onSubmit={(entities) => {
            onUpsert(entities[0]!, editEntity.slug);
            setEditEntity(null);
          }}
        />
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        variant="danger"
        title={t('moduleTemplateEditor.deleteEntityTitle')}
        message={
          pendingDelete
            ? t('moduleTemplateEditor.deleteEntityMessage', { name: pendingDelete.name })
            : ''
        }
        confirmLabel={t('common.delete')}
        onConfirm={() => {
          if (pendingDelete) onRemove(pendingDelete.slug);
          setPendingDelete(null);
        }}
        onClose={() => setPendingDelete(null)}
      />

      <ConfirmDialog
        open={pendingDeleteMenu !== null}
        variant="danger"
        title={t('moduleTemplateEditor.menuPane.deleteMenuTitle', { defaultValue: 'Usunąć menu?' })}
        message={
          pendingDeleteMenu
            ? t('moduleTemplateEditor.menuPane.deleteMenuMessage', {
                name: pendingDeleteMenu.name,
                defaultValue: `Czy na pewno usunąć menu „${pendingDeleteMenu.name}"?`,
              })
            : ''
        }
        confirmLabel={t('common.delete')}
        onConfirm={() => {
          if (pendingDeleteMenu) onDeleteMenu(pendingDeleteMenu.id);
          setPendingDeleteMenu(null);
        }}
        onClose={() => setPendingDeleteMenu(null)}
      />
    </aside>
  );
}

function SortableEntityRow({
  entity,
  selected,
  hasError,
  readOnly = false,
  onSelect,
  onEdit,
  onDelete,
}: {
  entity: ModuleTemplateEntityDefinition;
  selected: boolean;
  hasError: boolean;
  readOnly?: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: entity.slug,
    disabled: readOnly,
  });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={[
        'group flex items-center gap-1 rounded-lg border px-2 py-1.5',
        isDragging ? 'opacity-50' : '',
        selected
          ? 'border-accent-400 bg-accent-50 dark:bg-accent-950/30'
          : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600',
      ].join(' ')}
    >
      {!readOnly && (
        <DragHandle
          ariaLabel={t('moduleTemplateEditor.dragEntity', { name: entity.name })}
          dragHandleProps={{ ...attributes, ...listeners }}
        />
      )}
      {hasError && (
        <span
          className="h-2 w-2 shrink-0 rounded-full bg-red-500"
          aria-label={t('moduleTemplateEditor.validation.entityHasError')}
          title={t('moduleTemplateEditor.validation.entityHasError')}
        />
      )}
      <button type="button" onClick={onSelect} className="min-w-0 flex-1 text-left">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-sm font-medium text-text-primary">{entity.name}</span>
          {(() => {
            const kind = kartotekaKindFromDdlStrategy(entity.ddlStrategy);
            return (
              <KartotekaBadge
                kind={kind}
                name={
                  kind === 'system'
                    ? t('kartoteka.kindShort.system')
                    : t('kartoteka.kindShort.custom')
                }
                className="shrink-0"
              />
            );
          })()}
        </div>
        <div className="truncate text-xs text-gray-500 dark:text-gray-400">
          {entity.ddlStrategy === 'reference_to_system_entity'
            ? t('moduleTemplateEditor.referenceEntitySummary')
            : entity.ddlStrategy === 'reference_to_creator_entity'
              ? t('moduleTemplateEditor.creatorReferenceEntitySummary')
              : entity.ddlStrategy === 'document_class'
                ? t('moduleTemplateEditor.documentClassEntitySummary')
                : t('moduleTemplateEditor.entitySummary', {
                    fields: entity.fields.length,
                    views: entity.views.length,
                  })}
        </div>
      </button>
      {!readOnly && (
        <div className="flex items-center opacity-0 transition-opacity group-hover:opacity-100">
          <button
            type="button"
            onClick={onEdit}
            className="rounded p-1 text-xs text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            {t('common.edit')}
          </button>
          {entity.isPublished ? (
            <button
              type="button"
              disabled
              className="cursor-not-allowed rounded p-1 text-gray-300 dark:text-gray-600"
              aria-label={t('moduleTemplateEditor.entityRow.deletePublishedTooltip')}
              title={t('moduleTemplateEditor.entityRow.deletePublishedTooltip')}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          ) : (
            <button
              type="button"
              onClick={onDelete}
              className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40"
              aria-label={t('common.delete')}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Szczegóły encji (prawy panel)
// ---------------------------------------------------------------------------

function EntityDetail({
  entity,
  templateSlug,
  structureDirty,
  readOnly = false,
  onChangeMeta,
  onChangeFields,
  onChangeViews,
  onViewConfigPersisted,
  onKartotekaLayoutSaved,
}: {
  entity: ModuleTemplateEntityDefinition;
  /** Slug szablonu — nawigacja do projektowania layoutów widoków (hub Krok 2). */
  templateSlug: string;
  /** Niezapisana struktura — projektowanie layoutów zablokowane (PATCH pracuje na zapisanej). */
  structureDirty: boolean;
  /** Hub Krok 5 — konto bez roli projektanta: meta/pola/widoki tylko do odczytu. */
  readOnly?: boolean;
  onChangeMeta: (patch: Partial<ModuleTemplateEntityDefinition>) => void;
  onChangeFields: (fields: ModuleTemplateFieldDefinition[]) => void;
  onChangeViews: (views: ModuleTemplateViewDefinition[]) => void;
  /**
   * Callback po udanym PATCH config widoku encji-odwołania (clobber guard).
   * Synchronizuje lokalny stan edytora BEZ setDirty=true — config już na serwerze.
   */
  onViewConfigPersisted: (
    viewSlug: string,
    config: { formConfig?: ModuleTemplateFormConfig; listConfig?: ListConfig },
  ) => void;
  /**
   * Etap 2 kartoteki — callback po udanym PUT kartoteka-layout (pełny override layoutu).
   * Synchronizuje `entity.kartotekaLayoutOverride` w lokalnym stanie edytora BEZ dirty.
   * entitySlug: slug encji której override zmieniono.
   */
  onKartotekaLayoutSaved: (entitySlug: string, layoutConfig: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation();
  // Zakładki: zamiast jednego długiego scrolla (ustawienia + pola + formularz + listy)
  // każdy aspekt rodzaju danych to osobne, wyraźne miejsce.
  const [tab, setTab] = useState<'fields' | 'form' | 'lists' | 'settings'>('fields');

  const navModeOptions = MODULE_TEMPLATE_NAV_MODES.map((m) => ({
    value: m,
    label: t(`moduleTemplateEditor.navMode.${m}`),
  }));
  const ddlOptions = MODULE_TEMPLATE_DDL_STRATEGIES.map((s) => ({
    value: s,
    label: t(`moduleTemplateEditor.ddlStrategy.${s}`),
  }));

  // Detal encji pokazuje już tylko „Pola". Projektowanie formularza i list przeniesione
  // do modalu „Widoki pozycji menu" (builder menu), tryb nawigacji ustala builder menu,
  // a strategia encji ustalana jest przy tworzeniu — zakładki Formularz/Listy/Ustawienia
  // zwinięte (docs/architecture/CODE_MAP.md, sekcja apps/admin — wiersz o edytorze
  // szablonu modułu). Bloki renderujące te zakładki pozostają nieosiągalne
  // (tab zawsze 'fields'); pełne wycięcie kodu = osobny porządkowy commit.
  const tabs = [{ key: 'fields' as const, label: t('moduleTemplateEditor.tabs.fields') }];

  // Encja-odwołanie nie ma własnej struktury — pola i układ żyją w kartotece-celu.
  // Zakładki Pola/Formularz/Listy pokazują tę kartotekę (read-only) zamiast pustek.
  const isReference = entity.ddlStrategy === 'reference_to_system_entity';
  // Etap 3 — odwołanie do kartoteki Kreatora (innego szablonu). Pola/układ pochodzą
  // z szablonu źródłowego; tu pokazujemy panel informacyjny (MVP: bez per-modułowego buildera).
  const isCreatorReference = entity.ddlStrategy === 'reference_to_creator_entity';
  // Kartoteka na bazie klasy dokumentu — rekordy w tabeli `documents`, bez własnych pól;
  // pokazujemy panel informacyjny zamiast edytora pól nowej tabeli.
  const isDocumentClass = entity.ddlStrategy === 'document_class';

  return (
    <div className="flex flex-col gap-4">
      {/* Pasek zakładek tylko gdy jest więcej niż jedna — przy samej „Pola" zbędny. */}
      {tabs.length > 1 && (
        <div className="flex items-center gap-1 border-b border-gray-200 dark:border-gray-700">
          {tabs.map((tb) => (
            <button
              key={tb.key}
              type="button"
              onClick={() => setTab(tb.key)}
              data-testid={`entity-tab-${tb.key}`}
              className={[
                '-mb-px border-b-2 px-3 py-2 text-sm',
                tab === tb.key
                  ? 'border-accent-500 font-medium text-gray-900 dark:text-gray-100'
                  : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200',
              ].join(' ')}
            >
              {tb.label}
            </button>
          ))}
        </div>
      )}

      {tab === 'fields' &&
        (isCreatorReference ? (
          <CreatorReferencePanel entity={entity} />
        ) : isDocumentClass ? (
          <DocumentClassPanel entity={entity} />
        ) : isReference ? (
          <ReferenceSystemKartotekaPanel
            referenceSlug={entity.referenceSystemModuleSlug}
            fallbackName={entity.name}
            readOnly={readOnly}
          />
        ) : (
          <NewTableKartotekaPanel
            entity={entity}
            onChangeFields={onChangeFields}
            readOnly={readOnly}
            structureDirty={structureDirty}
          />
        ))}

      {tab === 'form' &&
        (isCreatorReference ? (
          <CreatorReferencePanel entity={entity} />
        ) : isReference ? (
          entity.referenceSystemModuleSlug ? (
            structureDirty ? (
              // Krok 3: PUT kartoteka-layout wymaga zapisanej struktury — guard blokujący builder
              <div
                className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
                data-testid="reference-form-save-structure-hint"
              >
                {t('moduleTemplateEditor.referenceBuilder.saveStructureFirst')}
              </div>
            ) : (
              // Etap 2 kartoteki: pełny builder formularza z paskiem zakładek (10 zakładek)
              // zamiast starego single-canvas ReferenceFormBuilderPanel.
              // Analogia: ReferenceListBuilderEmbedded reużywa SystemModuleListBuilderPage.
              <ReferenceFormBuilderEmbedded
                templateCode={templateSlug}
                entitySlug={entity.slug}
                kartotekaSlug={entity.referenceSystemModuleSlug}
                kartotekaLayoutOverride={entity.kartotekaLayoutOverride}
                onLayoutSaved={(layoutConfig) => onKartotekaLayoutSaved(entity.slug, layoutConfig)}
              />
            )
          ) : null
        ) : (
          <ViewsSection
            kind="form"
            entity={entity}
            onChange={onChangeViews}
            templateSlug={templateSlug}
            structureDirty={structureDirty}
            readOnly={readOnly}
          />
        ))}

      {tab === 'lists' &&
        (isCreatorReference ? (
          <CreatorReferencePanel entity={entity} />
        ) : isReference ? (
          entity.referenceSystemModuleSlug ? (
            structureDirty ? (
              // Krok 3: PATCH wymaga zapisanej struktury — guard blokujący kreator
              <div
                className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
                data-testid="reference-list-save-structure-hint"
              >
                {t('moduleTemplateEditor.referenceBuilder.saveStructureFirst')}
              </div>
            ) : (
              <ReferenceListBuilderEmbedded
                templateCode={templateSlug}
                entitySlug={entity.slug}
                kartotekaSlug={entity.referenceSystemModuleSlug}
                onViewConfigPersisted={(listConfig) =>
                  onViewConfigPersisted('list', { listConfig })
                }
              />
            )
          ) : null
        ) : (
          <ViewsSection
            kind="collection"
            entity={entity}
            onChange={onChangeViews}
            templateSlug={templateSlug}
            structureDirty={structureDirty}
            readOnly={readOnly}
          />
        ))}

      {tab === 'settings' && (
        <section className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
          <h3 className="mb-3 text-sm font-semibold">{t('moduleTemplateEditor.entityMeta')}</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField label={t('moduleTemplateEditor.field.navMode')}>
              <Dropdown
                options={navModeOptions}
                value={entity.navigationMode}
                disabled={readOnly}
                onChange={(v) => onChangeMeta({ navigationMode: v as ModuleTemplateNavMode })}
              />
            </FormField>
            <FormField label={t('moduleTemplateEditor.field.ddlStrategy')}>
              <Dropdown
                options={ddlOptions}
                value={entity.ddlStrategy}
                disabled={readOnly}
                onChange={(v) => onChangeMeta({ ddlStrategy: v as ModuleTemplateDdlStrategy })}
              />
            </FormField>
            {entity.ddlStrategy === 'reference_to_system_entity' && (
              <Input
                label={t('moduleTemplateEditor.field.referenceSystemModuleSlug')}
                hint={t('moduleTemplateEditor.field.referenceSystemModuleSlugHint')}
                value={entity.referenceSystemModuleSlug ?? ''}
                disabled={readOnly}
                onChange={(e) => onChangeMeta({ referenceSystemModuleSlug: e.target.value })}
                placeholder="np. employees"
              />
            )}
            {/* Etap 3 — cel odwołania Kreatora (read-only; ustalany w pickerze „Nowa kartoteka"). */}
            {entity.ddlStrategy === 'reference_to_creator_entity' && (
              <Input
                label={t('moduleTemplateEditor.field.referenceCreatorTarget')}
                hint={t('moduleTemplateEditor.field.referenceCreatorTargetHint')}
                value={`${entity.referenceTemplateCode ?? '—'} / ${entity.referenceTargetEntitySlug ?? '—'}`}
                disabled
              />
            )}
          </div>
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel encji-odwołania do kartoteki Kreatora (Etap 3) — read-only info.
//
// Encja `reference_to_creator_entity` nie ma własnej struktury — pola i układ żyją
// w kartotece źródłowej (encji `new_table` innego szablonu). U klienta po instalacji
// to zwykły moduł custom z własnym układem. Zamiast pustego buildera pokazujemy skąd
// pochodzi kartoteka. (MVP: bez per-modułowego buildera układu dla odwołań Kreatora.)
// ---------------------------------------------------------------------------

function CreatorReferencePanel({ entity }: { entity: ModuleTemplateEntityDefinition }) {
  const { t } = useTranslation();
  return (
    <div
      className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-200"
      data-testid="creator-reference-panel"
    >
      <p className="font-medium">{t('moduleTemplateEditor.creatorReferencePanel.title')}</p>
      <p className="mt-1">
        {t('moduleTemplateEditor.creatorReferencePanel.body', {
          template: entity.referenceTemplateCode ?? '—',
          entity: entity.referenceTargetEntitySlug ?? '—',
        })}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel encji na bazie klasy dokumentu (document_class) — read-only.
//
// Encja `document_class` nie ma własnej tabeli — rekordy to wiersze wspólnej tabeli
// `documents` zawężone do klasy + systemowego typu. Pola „tej kartoteki" żyją w systemowym
// TYPIE dokumentu; pokazujemy je tu tylko do wglądu (edycja na ekranie Kartoteki → typ).
//
// Pola pobieramy tym samym mechanizmem co modal „Pola typu" na ekranie Kartoteki:
// GET /admin/kartoteki/{slug}/definition?typeId=. UWAGA: `slug` to slug kartoteki systemowej
// (SEED_MODULES, np. 'contracts'), a nie sama wartość klasy ('contract') — mapowanie
// kanoniczne z DOCUMENT_KARTOTEKI (@saas/shared). Widok typu zwraca pola odziedziczone z klasy
// (`inherited:true`, read-only) + pola prywatne typu (`inherited:false`).
// ---------------------------------------------------------------------------

/** Wiersz pola typu dokumentu — read-only (nazwa + slug + autor/rdzeń + typ pola). */
function DocumentClassFieldRow({ field: f }: { field: KartotekaBuilderField }) {
  const { t } = useTranslation();
  return (
    <li
      className="flex flex-wrap items-center gap-2 py-1.5 text-sm"
      data-testid="document-class-field-item"
    >
      <span className="text-text-primary">{f.name}</span>
      <code className="text-xs text-gray-400">{f.slug}</code>
      {f.isCore ? (
        <Badge color="gray">{t('moduleTemplateEditor.referencePanel.coreBadge')}</Badge>
      ) : (
        <>
          <Badge color="green">{t('moduleTemplateEditor.referencePanel.addedFieldBadge')}</Badge>
          {/* Autor luźnym tekstem obok badge'a (nie w badge'u). */}
          <span className="text-xs text-gray-400">
            {t('moduleTemplateEditor.referencePanel.addedByText', {
              name: f.addedByName ?? t('moduleTemplateEditor.referencePanel.addedByUnknown'),
            })}
          </span>
        </>
      )}
      <code className="ml-auto text-xs text-gray-500 dark:text-gray-400">{f.fieldType}</code>
    </li>
  );
}

function DocumentClassPanel({ entity }: { entity: ModuleTemplateEntityDefinition }) {
  const { t } = useTranslation();
  const classLabel = entity.documentClass
    ? t(`adminDocumentTypes.classes.${entity.documentClass}`)
    : '—';

  // Klasa dokumentu → slug kartoteki systemowej (endpoint definicji rozwiązuje slug po
  // SEED_MODULES, nie po wartości klasy). 'contract' → 'contracts', 'internal' → 'internal'.
  const kartotekaSlug = DOCUMENT_KARTOTEKI.find(
    (d) => d.documentClass === entity.documentClass,
  )?.slug;
  const typeId = entity.documentGlobalTypeId;
  const typeMode = Boolean(typeId);

  const definitionQuery = useKartotekaDefinition(kartotekaSlug, typeId);
  const fields = definitionQuery.data?.fields ?? [];
  const inheritedFields = fields.filter((f) => f.inherited);
  const ownFields = fields.filter((f) => !f.inherited);

  const emptyState = (
    <p className="text-sm text-text-muted">{t('moduleTemplateEditor.referencePanel.noFields')}</p>
  );

  return (
    <div className="flex flex-col gap-4">
      <div
        className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-200"
        data-testid="document-class-panel"
      >
        <p className="font-medium">{t('moduleTemplateEditor.documentClassPanel.title')}</p>
        <p className="mt-1">
          {t('moduleTemplateEditor.documentClassPanel.body', { class: classLabel })}
        </p>
      </div>

      <section
        className="rounded-lg border border-gray-200 p-4 dark:border-gray-700"
        data-testid="document-class-fields"
      >
        <h3 className="text-sm font-semibold">
          {t('moduleTemplateEditor.documentClassPanel.fieldsHeader')}
        </h3>
        <p className="mb-3 mt-1 text-xs text-text-muted">
          {t('moduleTemplateEditor.documentClassPanel.editHint', { class: classLabel })}
        </p>

        {definitionQuery.isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-3/4" />
          </div>
        )}

        {definitionQuery.isError && (
          <p className="text-sm text-red-600 dark:text-red-400">
            {t('moduleTemplateEditor.referencePanel.loadError')}
          </p>
        )}

        {!definitionQuery.isLoading &&
          !definitionQuery.isError &&
          (typeMode ? (
            <div className="space-y-6">
              <div>
                <h4 className="mb-2 text-sm font-semibold text-text-secondary">
                  {t('moduleTemplateEditor.referencePanel.inheritedSectionTitle')}
                </h4>
                {inheritedFields.length === 0 ? (
                  emptyState
                ) : (
                  <ul
                    className="divide-y divide-gray-100 dark:divide-gray-700"
                    data-testid="document-class-inherited-fields"
                  >
                    {inheritedFields.map((f) => (
                      <DocumentClassFieldRow key={f.id} field={f} />
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <h4 className="mb-2 text-sm font-semibold text-text-secondary">
                  {t('moduleTemplateEditor.referencePanel.ownTypeSectionTitle')}
                </h4>
                {ownFields.length === 0 ? (
                  emptyState
                ) : (
                  <ul
                    className="divide-y divide-gray-100 dark:divide-gray-700"
                    data-testid="document-class-own-type-fields"
                  >
                    {ownFields.map((f) => (
                      <DocumentClassFieldRow key={f.id} field={f} />
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ) : fields.length === 0 ? (
            emptyState
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {fields.map((f) => (
                <DocumentClassFieldRow key={f.id} field={f} />
              ))}
            </ul>
          ))}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Osadzony kreator listy encji-odwołania (pełny SystemModuleListBuilderPage).
//
// Opakowuje SystemModuleListBuilderPage w ReferenceListContext — dostarcza pola
// kartoteki (etykiety kolumn) i callback onViewConfigPersisted (clobber guard).
// Wyekstrahowany do osobnej funkcji, żeby uniknąć warunkowego wywołania hooka
// (useKartotekaDefinition) wewnątrz EntityDetail.
// ---------------------------------------------------------------------------

function ReferenceListBuilderEmbedded({
  templateCode,
  entitySlug,
  kartotekaSlug,
  onViewConfigPersisted,
}: {
  templateCode: string;
  entitySlug: string;
  kartotekaSlug: string;
  onViewConfigPersisted: (listConfig: ListConfig) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const kartotekaQuery = useKartotekaDefinition(kartotekaSlug);
  const kartotekaFields = kartotekaQuery.data?.fields ?? [];
  // Etap 2 B — przekazujemy defaultListConfig z API kartoteki do kontekstu,
  // żeby useTableLayout mógł seedować listę domyślnymi kolumnami (showInList
  // z definicji kartoteki) zamiast generować je ze wszystkich pól.
  const kartotekaDefaultListConfig = kartotekaQuery.data?.defaultListConfig;

  // W4 Faza 1b: osobny przycisk „Wyślij widok listy do klientów" (decyzja produktowa —
  // formularz i lista propagowane niezależnie). Pcha zapisany widok listy kartoteki do
  // aktywnych tenantów; kolumny adresowane slugiem trafiają 1:1 (bez przepisywania pól).
  const [confirmPublish, setConfirmPublish] = useState(false);
  const publishMutation = useMutation({
    mutationFn: () => publishKartotekaList(templateCode, entitySlug),
    onSuccess: (data) => {
      toast.show({
        variant: data.totals.failed > 0 ? 'warning' : 'success',
        title:
          data.totals.failed > 0
            ? t('moduleTemplateEditor.referencePanel.publishList.partialSuccess', {
                succeeded: data.totals.succeeded,
                failed: data.totals.failed,
              })
            : t('moduleTemplateEditor.referencePanel.publishList.success', {
                count: data.totals.succeeded,
              }),
      });
    },
    onError: (err) => {
      // 422 NO_LIST_VIEW → widok listy nieuzupełniony (najpierw ułóż kolumny).
      const code = extractTemplateApiErrorCode(err);
      toast.show({
        variant: 'error',
        title:
          code === 'NO_LIST_VIEW'
            ? t('moduleTemplateEditor.referencePanel.publishList.noListView')
            : t('moduleTemplateEditor.referencePanel.publishList.error'),
      });
    },
  });

  return (
    <>
      <div className="mb-3 flex justify-end">
        <Button
          size="sm"
          variant="primary"
          data-testid="publish-kartoteka-list-btn"
          disabled={publishMutation.isPending}
          onClick={() => setConfirmPublish(true)}
        >
          {t('moduleTemplateEditor.referencePanel.publishList.button')}
        </Button>
      </div>

      <ReferenceListContext.Provider
        value={{
          kartotekaFields,
          kartotekaDefaultListConfig,
          onViewConfigPersisted,
          // Domyślny widok listy encji-odwołania (reference_to_system_entity) — pola żyją
          // w kartotece systemowej, bez fizycznej kolumny nazwy/numeru rekordu.
          builtinColumnsAvailable: false,
        }}
      >
        <SystemModuleListBuilderPage
          scope="template"
          embeddedMode
          code={templateCode}
          entitySlug={entitySlug}
          viewSlug="list"
        />
      </ReferenceListContext.Provider>

      <ConfirmDialog
        open={confirmPublish}
        variant="primary"
        title={t('moduleTemplateEditor.referencePanel.publishList.confirmTitle')}
        message={t('moduleTemplateEditor.referencePanel.publishList.confirmMessage')}
        confirmLabel={t('moduleTemplateEditor.referencePanel.publishList.button')}
        onConfirm={() => {
          setConfirmPublish(false);
          publishMutation.mutate();
        }}
        onClose={() => setConfirmPublish(false)}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Etap 2 kartoteki — pełny builder formularza kartoteki systemowej.
//
// Analogia do ReferenceListBuilderEmbedded (wzorzec list-builder).
// Opakowuje SystemModuleBuilderPage z propami override:
//  - `slug` = kartotekaSlug (np. 'contractors') — builda w przestrzeni kartoteki
//  - `initialLayoutConfig` = kartotekaLayoutOverride || domyślny layout kartoteki
//  - `onSaveKartotekaLayout` → PUT kartoteka-layout, callback onLayoutSaved
//  - `readOnlyTabTypes` — typy zakładek read-only w trybie super admina
//
// READ-ONLY: tree_picker, satellite, additional, panel, audit_log, approvals,
//            subcollection z kolekcją 'contractor_company_access'.
// EDYTOWALNE: fields (Dane), subcollection (Kontakty, Adresy, Konta bankowe).
// ---------------------------------------------------------------------------

const KARTOTEKA_READ_ONLY_TAB_TYPES: Array<
  'tree_picker' | 'satellite' | 'additional' | 'panel' | 'audit_log' | 'approvals'
> = ['tree_picker', 'satellite', 'additional', 'panel', 'audit_log', 'approvals'];

/**
 * Kolekcje zakładek `subcollection` read-only w trybie super admina (Etap 2 kartoteki).
 * Zakładki „Dostępy firm" — zarządzane przez logikę biznesową modułu, nie edytowalne
 * w builderze formularza. Dotyczy wszystkich kartotek mających *_company_access.
 *
 * Edytowalne pozostają: Kontakty (contractor_contacts), Adresy (addresses),
 * Konta bankowe (contractor_bank_accounts), Struktura organizacyjna (org_units) itd.
 */
const KARTOTEKA_READ_ONLY_COLLECTIONS: string[] = [
  'contractor_company_access',
  'user_company_access',
];

function ReferenceFormBuilderEmbedded({
  templateCode,
  entitySlug,
  kartotekaSlug,
  kartotekaLayoutOverride,
  onLayoutSaved,
}: {
  templateCode: string;
  entitySlug: string;
  kartotekaSlug: string;
  /**
   * Per-modułowy override layoutu (z `entity.kartotekaLayoutOverride`).
   * undefined/null → użyj domyślnego layoutu z definicji kartoteki.
   */
  kartotekaLayoutOverride?: Record<string, unknown>;
  /** Callback po udanym PUT — synchronizuje stan edytora BEZ dirty. */
  onLayoutSaved: (layoutConfig: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const kartotekaQuery = useKartotekaDefinition(kartotekaSlug);

  const [confirmPublish, setConfirmPublish] = useState(false);
  // S7: po publikacji przemontowujemy builder, by od razu odświeżył znaczniki
  // publikacji zakładek (publishedAt) z ostemplowanego override.
  const [builderRemountKey, setBuilderRemountKey] = useState(0);

  // Priorytet layoutu: override > domyślny z definicji kartoteki > pusty.
  // Stabilny obiekt (nie re-tworzymy per render) — hook useMemo.
  const initialLayoutConfig = useMemo(() => {
    if (kartotekaLayoutOverride && Object.keys(kartotekaLayoutOverride).length > 0) {
      return kartotekaLayoutOverride;
    }
    const defaultLayout = kartotekaQuery.data?.layouts.find(
      (l) => l.layoutType === 'form' && l.isDefault,
    );
    if (defaultLayout?.layoutConfig && typeof defaultLayout.layoutConfig === 'object') {
      return defaultLayout.layoutConfig as Record<string, unknown>;
    }
    // Pusty canvas — builder zacznie od zera
    return { elements: [] };
  }, [kartotekaLayoutOverride, kartotekaQuery.data]);

  const handleSave = async (layoutConfig: Record<string, unknown>) => {
    await putKartotekaLayoutOverride(templateCode, entitySlug, layoutConfig);
    onLayoutSaved(layoutConfig);
    toast.show({
      variant: 'success',
      description: t('moduleTemplateEditor.referenceFormBuilder.savedToast', {
        defaultValue: 'Layout kartoteki zapisany.',
      }),
    });
  };

  const publishMutation = useMutation({
    mutationFn: () => publishKartotekaLayout(templateCode, entitySlug),
    onSuccess: (data) => {
      // S7: synchronizuj ostemplowany override + przemontuj builder, by zakładki
      // opublikowane od razu zablokowały kosz/zmianę nazwy (bez ręcznego reloadu).
      if (data.kartotekaLayoutOverride) {
        onLayoutSaved(data.kartotekaLayoutOverride);
        setBuilderRemountKey((k) => k + 1);
      }
      // Publikacja stempluje first_published_at na polach z układu → zakładka „Pola"
      // (KartotekaFieldsManager) musi odświeżyć definicję, inaczej zostaje nieaktualny
      // kosz przy polu, które właśnie stało się opublikowane (klik → 409).
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'kartoteki', kartotekaSlug, 'definition'],
      });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'kartoteki', kartotekaSlug] });
      toast.show({
        variant: data.totals.failed > 0 ? 'warning' : 'success',
        title:
          data.totals.failed > 0
            ? t('moduleTemplateEditor.referencePanel.publishLayout.partialSuccess', {
                succeeded: data.totals.succeeded,
                failed: data.totals.failed,
              })
            : t('moduleTemplateEditor.referencePanel.publishLayout.success', {
                count: data.totals.succeeded,
              }),
      });
    },
    onError: () => {
      toast.show({
        variant: 'error',
        title: t('moduleTemplateEditor.referencePanel.publishLayout.error'),
      });
    },
  });

  if (kartotekaQuery.isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (kartotekaQuery.isError) {
    return (
      <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300">
        {t('moduleTemplateEditor.referenceFormBuilder.loadError', {
          defaultValue: 'Nie udało się załadować definicji kartoteki.',
        })}
      </div>
    );
  }

  return (
    <>
      <div className="mb-3 flex justify-end">
        <Button
          size="sm"
          variant="primary"
          data-testid="publish-kartoteka-layout-btn"
          disabled={publishMutation.isPending}
          onClick={() => setConfirmPublish(true)}
        >
          {t('moduleTemplateEditor.referencePanel.publishLayout.button')}
        </Button>
      </div>

      <div
        className="w-full overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700"
        data-testid="reference-form-builder-embedded"
      >
        <SystemModuleBuilderPage
          key={builderRemountKey}
          embeddedMode
          slug={kartotekaSlug}
          initialLayoutConfig={initialLayoutConfig}
          onSaveKartotekaLayout={handleSave}
          // Fix 401: „Dodaj pole" w scope admina idzie adminową ścieżką kartoteki
          // (POST /admin/kartoteki/:slug/fields), nie tenantową /system-modules.
          addFieldKartotekaSlug={kartotekaSlug}
          readOnlyTabTypes={KARTOTEKA_READ_ONLY_TAB_TYPES}
          allowTabManagement
          readOnlyTabCollections={KARTOTEKA_READ_ONLY_COLLECTIONS}
          externalFields={kartotekaQuery.data?.fields}
          externalHelperFields={kartotekaQuery.data?.helperFields}
          externalHelperLayouts={kartotekaQuery.data?.helperDefaultLayouts}
        />
      </div>

      <ConfirmDialog
        open={confirmPublish}
        variant="primary"
        title={t('moduleTemplateEditor.referencePanel.publishLayout.confirmTitle')}
        message={t('moduleTemplateEditor.referencePanel.publishLayout.confirmMessage')}
        confirmLabel={t('moduleTemplateEditor.referencePanel.publishLayout.button')}
        onConfirm={() => {
          setConfirmPublish(false);
          publishMutation.mutate();
        }}
        onClose={() => setConfirmPublish(false)}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Panel encji-odwołania do kartoteki systemowej (read-only).
//
// Encja `reference_to_system_entity` nie ma własnych pól/widoków — wskazuje kartotekę
// systemową. Zamiast „0 pól" pokazujemy realną kartotekę (pola read-only) i kierujemy
// do jej edycji na poziomie kartoteki (wspólnej dla wszystkich modułów, które jej używają).
// ---------------------------------------------------------------------------

interface SystemKartotekaDetail {
  slug: string;
  name: string;
  division: 'kategorie' | 'klasy';
  fieldCount: number;
  fields: { name: string; fieldType: string }[];
}

function ReferenceSystemKartotekaPanel({
  referenceSlug,
  fallbackName,
  readOnly = false,
}: {
  referenceSlug?: string;
  fallbackName: string;
  /** Hub Krok 5 — opublikowany szablon read-only: bez przycisku „Dodaj pole". */
  readOnly?: boolean;
}) {
  const { t } = useTranslation();

  const query = useQuery<{ kartoteka: SystemKartotekaDetail }>({
    queryKey: ['admin', 'kartoteki', referenceSlug],
    queryFn: async () =>
      (await api.get<{ kartoteka: SystemKartotekaDetail }>(`/admin/kartoteki/${referenceSlug}`))
        .data,
    enabled: Boolean(referenceSlug),
    retry: false,
  });

  const k = query.data?.kartoteka;
  const name = k?.name ?? fallbackName;

  return (
    <section className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold">{name}</h3>
        <Badge color="green">{t('moduleTemplateEditor.referencePanel.systemBadge')}</Badge>
        {k && (
          <Badge color={k.division === 'klasy' ? 'amber' : 'gray'}>
            {t(
              k.division === 'klasy'
                ? 'moduleTemplateEditor.referencePanel.divisionKlasy'
                : 'moduleTemplateEditor.referencePanel.divisionKategorie',
            )}
          </Badge>
        )}
      </div>

      <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
        {t('moduleTemplateEditor.referencePanel.description', { name })}
      </p>

      {/* E2.2b — wspólny panel pól (lista + Dodaj pole). */}
      {referenceSlug && <KartotekaFieldsManager slug={referenceSlug} readOnly={readOnly} />}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Helper — licznik pozycji menu (rekurencja w drzewie)
// ---------------------------------------------------------------------------

function countMenuItems(items: MenuItem[]): number {
  return items.reduce((sum, item) => sum + 1 + countMenuItems(item.children ?? []), 0);
}

// ---------------------------------------------------------------------------
// Detal kartoteki własnej (new_table) — 3 zakładki jak w kartotece systemowej
// (KartotekaFieldsManager): Pola / Layouty formularzy / Widoki list.
//
// Faza 2 (ADR-0060): zakładka „Layouty formularzy" to pełny CRUD biblioteki widoków
// (`KartotekaFormViewsPanel`, tożsamość creator = UUID encji) — parytet z kartoteką
// systemową. Widoki żyjące TYLKO per-szablon (domyślny + legacy sprzed biblioteki)
// pokazujemy pod spodem read-only (zarządza nimi builder menu). Zakładka „Widoki list"
// zostaje read-only z `entity.views` (loader scala bibliotekę z per-szablon).
// CRUD wymaga UUID encji i zapisanej struktury (walidacja configu bije w pola z ADM)
// — inaczej hint „zapisz strukturę".
// ---------------------------------------------------------------------------

export function NewTableKartotekaPanel({
  entity,
  onChangeFields,
  readOnly = false,
  structureDirty = false,
}: {
  entity: ModuleTemplateEntityDefinition;
  onChangeFields: (fields: ModuleTemplateFieldDefinition[]) => void;
  readOnly?: boolean;
  /** Niezapisane zmiany struktury — CRUD widoków pracuje na zapisanej wersji (ADM). */
  structureDirty?: boolean;
}) {
  const { t } = useTranslation();

  const formViews = entity.views.filter((v) => v.type === 'form');
  const listViews = entity.views.filter((v) => v.type !== 'form');

  // Tożsamość biblioteki widoków: UUID encji (slug jest edytowalny). Świeża encja
  // przed pierwszym zapisem struktury nie ma UUID → CRUD zablokowany hintem.
  const identity = entity.id ? ({ kind: 'creator', key: entity.id } as const) : undefined;
  const libraryViewsQuery = useKartotekaLibraryViews(identity);

  // Pola encji szablonu w kształcie interchange panelu widoków (bogaty builder operuje
  // na surowych typach pól — bez mapowania na paletę starego FieldRegistry).
  const builderFields: KartotekaBuilderField[] = entity.fields.map((f, idx) => ({
    id: f.slug,
    slug: f.slug,
    name: f.name,
    fieldType: f.type,
    isCore: false,
    isSystemLocked: false,
    isRequired: f.isRequired,
    sortOrder: f.sortOrder ?? idx,
  }));

  // Widoki form żyjące TYLKO per-szablon (domyślny + legacy sprzed biblioteki) —
  // liczone dopiero gdy biblioteka wczytana, żeby nie mignąć pełną listą.
  const librarySlugs = new Set((libraryViewsQuery.data ?? []).map((v) => v.slug));
  const templateOnlyFormViews = libraryViewsQuery.data
    ? formViews.filter((v) => !librarySlugs.has(v.slug))
    : [];

  const renderViews = (
    views: ModuleTemplateViewDefinition[],
    emptyKey: string | null,
    showType: boolean,
  ) =>
    views.length === 0 ? (
      emptyKey ? (
        <p className="text-sm text-text-muted">{t(emptyKey)}</p>
      ) : null
    ) : (
      <ul className="divide-y divide-gray-100 dark:divide-gray-700">
        {views.map((v) => (
          <li
            key={v.slug}
            className="flex flex-wrap items-center gap-2 py-2 text-sm"
            data-testid="entity-view-item"
          >
            <span className="font-medium text-text-primary">{v.name}</span>
            <code className="text-xs text-gray-400 dark:text-gray-500">{v.slug}</code>
            {showType && <Badge color="gray">{t(`moduleTemplateEditor.viewType.${v.type}`)}</Badge>}
            {v.isDefault && <Badge color="green">{t('moduleTemplateEditor.defaultView')}</Badge>}
          </li>
        ))}
      </ul>
    );

  const saveStructureHint = (
    <div
      className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
      data-testid="creator-views-save-structure-hint"
    >
      {t('moduleTemplateEditor.referenceBuilder.saveStructureFirst')}
    </div>
  );

  const formViewsContent = (
    <div className="flex flex-col gap-4">
      {!identity || structureDirty ? (
        saveStructureHint
      ) : (
        <KartotekaFormViewsPanel
          identity={identity}
          fields={builderFields}
          viewsQuery={libraryViewsQuery}
          readOnly={readOnly}
        />
      )}
      {templateOnlyFormViews.length > 0 && (
        <div data-testid="template-only-form-views">
          <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
            {t('moduleTemplateEditor.referencePanel.templateOnlyViewsHeader')}
          </h4>
          {renderViews(templateOnlyFormViews, null, false)}
        </div>
      )}
    </div>
  );

  const tabItems = [
    {
      value: 'fields',
      label: `${t('moduleTemplateEditor.referencePanel.tabFields')} (${entity.fields.length})`,
      content: <FieldsSection entity={entity} onChange={onChangeFields} readOnly={readOnly} />,
    },
    {
      value: 'formViews',
      label: `${t('moduleTemplateEditor.referencePanel.tabFormViews')} (${formViews.length})`,
      content: formViewsContent,
    },
    {
      value: 'listViews',
      label: `${t('moduleTemplateEditor.referencePanel.tabListViews')} (${listViews.length})`,
      content: renderViews(listViews, 'moduleTemplateEditor.referencePanel.noListViews', true),
    },
  ];

  return (
    <div data-testid="new-table-kartoteka-panel">
      <Tabs idPrefix={`entity-${entity.slug}`} defaultValue="fields" items={tabItems} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sekcja pól
// ---------------------------------------------------------------------------

function FieldsSection({
  entity,
  onChange,
  readOnly = false,
}: {
  entity: ModuleTemplateEntityDefinition;
  onChange: (fields: ModuleTemplateFieldDefinition[]) => void;
  /** Hub Krok 5 — read-only: bez „dodaj pole" / edycji / usuwania / przeciągania. */
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const fields = entity.fields;
  const [createOpen, setCreateOpen] = useState(false);
  const [editField, setEditField] = useState<ModuleTemplateFieldDefinition | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const from = fields.findIndex((f) => f.slug === active.id);
      const to = fields.findIndex((f) => f.slug === over.id);
      if (from >= 0 && to >= 0) onChange(arrayMove(fields, from, to));
    }
  };

  const existingSlugs = fields.map((f) => f.slug);
  const otherEntitySlugs: string[] = []; // referencje cross-entity — wolny wpis w v1
  const atLimit = fields.length >= MODULE_TEMPLATE_MAX_FIELDS_PER_ENTITY;

  // Bez własnego nagłówka — sekcja renderuje się w zakładce „Pola kartoteki"
  // (NewTableKartotekaPanel), która już nazywa i liczy zawartość. Przycisk „Dodaj pole"
  // wyrównany do prawej jak w panelu kartoteki systemowej (FieldsTabPanel).
  return (
    <section>
      {!readOnly && (
        <div className="mb-3 flex justify-end">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setCreateOpen(true)}
            disabled={atLimit}
            title={atLimit ? t('moduleTemplateEditor.fieldLimit') : undefined}
          >
            <Plus className="mr-1 h-4 w-4" />
            {t('moduleTemplateEditor.addField')}
          </Button>
        </div>
      )}

      {fields.length === 0 ? (
        <p className="rounded border border-dashed border-gray-300 p-4 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
          {t('moduleTemplateEditor.noFields')}
        </p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={existingSlugs} strategy={verticalListSortingStrategy}>
            <ul className="flex flex-col gap-1">
              {fields.map((field) => (
                <SortableFieldRow
                  key={field.slug}
                  field={field}
                  readOnly={readOnly}
                  onEdit={() => setEditField(field)}
                  onDelete={() => onChange(fields.filter((f) => f.slug !== field.slug))}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      <FieldFormModal
        open={createOpen}
        mode="create"
        existingSlugs={existingSlugs}
        siblingFields={fields}
        referenceTargets={otherEntitySlugs}
        onClose={() => setCreateOpen(false)}
        onSubmit={(field) => {
          onChange([...fields, field]);
          setCreateOpen(false);
        }}
      />

      {editField && (
        <FieldFormModal
          open
          mode="edit"
          field={editField}
          existingSlugs={existingSlugs.filter((s) => s !== editField.slug)}
          siblingFields={fields.filter((f) => f.slug !== editField.slug)}
          referenceTargets={otherEntitySlugs}
          onClose={() => setEditField(null)}
          onSubmit={(field) => {
            onChange(fields.map((f) => (f.slug === editField.slug ? field : f)));
            setEditField(null);
          }}
        />
      )}
    </section>
  );
}

function SortableFieldRow({
  field,
  readOnly = false,
  onEdit,
  onDelete,
}: {
  field: ModuleTemplateFieldDefinition;
  readOnly?: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.slug,
    disabled: readOnly,
  });

  // Add-only po publikacji: pole z opublikowanego snapshotu jest zamrożone (bez edycji
  // i usuwania — lustro bramy PUT structure). Reorder zostaje dozwolony (drag handle),
  // zgodnie z zasadą „opublikowane = add + reorder only".
  const isPublishedField = Boolean(field.isPublished);

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={[
        'group flex items-center gap-2 rounded-lg border border-gray-200 px-2 py-1.5 dark:border-gray-700',
        isDragging ? 'opacity-50' : '',
      ].join(' ')}
    >
      {!readOnly && (
        <DragHandle
          ariaLabel={t('moduleTemplateEditor.dragField', { name: field.name })}
          dragHandleProps={{ ...attributes, ...listeners }}
        />
      )}
      {isPublishedField && (
        <Lock
          className="h-3.5 w-3.5 shrink-0 text-gray-400"
          aria-label={t('moduleTemplateEditor.fieldPublishedLockedHint')}
          data-testid="field-published-lock"
        />
      )}
      <button
        type="button"
        onClick={readOnly || isPublishedField ? undefined : onEdit}
        disabled={readOnly || isPublishedField}
        title={
          !readOnly && isPublishedField
            ? t('moduleTemplateEditor.fieldPublishedLockedHint')
            : undefined
        }
        className="min-w-0 flex-1 text-left"
      >
        <span className="text-sm font-medium">{field.name}</span>
        <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
          <code>{field.slug}</code>
        </span>
      </button>
      <Badge color="gray">{t(`moduleTemplateEditor.fieldType.${field.type}`)}</Badge>
      {field.isRequired && <Badge color="amber">{t('moduleTemplateEditor.required')}</Badge>}
      {!readOnly && !isPublishedField && (
        <button
          type="button"
          onClick={onDelete}
          className="rounded p-1 text-gray-400 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 group-hover:opacity-100 dark:hover:bg-red-950/40"
          aria-label={t('common.delete')}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Sekcja widoków
// ---------------------------------------------------------------------------

function ViewsSection({
  entity,
  onChange,
  templateSlug,
  structureDirty,
  readOnly = false,
  kind,
}: {
  entity: ModuleTemplateEntityDefinition;
  onChange: (views: ModuleTemplateViewDefinition[]) => void;
  templateSlug: string;
  structureDirty: boolean;
  /** Hub Krok 5 — read-only: bez „dodaj widok" / edycji / projektowania layoutu / usuwania. */
  readOnly?: boolean;
  /** Która grupa: 'form' = formularze (1 rekord), 'collection' = listy i widoki (wiele). */
  kind: 'form' | 'collection';
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const views = entity.views;
  const [createType, setCreateType] = useState<ModuleTemplateViewType | null>(null);
  const [editView, setEditView] = useState<ModuleTemplateViewDefinition | null>(null);
  // Podgląd na żywo (makieta) — który widok jest aktualnie otwarty w PreviewModal.
  const [previewView, setPreviewView] = useState<ModuleTemplateViewDefinition | null>(null);

  const existingSlugs = views.map((v) => v.slug);
  const atLimit = views.length >= MODULE_TEMPLATE_MAX_VIEWS_PER_ENTITY;

  // Rozdział widoków na dwa cele: formularz = pojedynczy rekord (dodawanie/edycja jednego),
  // listy i widoki = przeglądanie wielu rekordów (lista + kalendarz/kanban/dashboard).
  const formViews = views.filter((v) => v.type === 'form');
  const collectionViews = views.filter((v) => v.type !== 'form');

  const renderView = (view: ModuleTemplateViewDefinition, showType: boolean) => (
    <li
      key={view.slug}
      className="group flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700"
    >
      <button
        type="button"
        onClick={readOnly ? undefined : () => setEditView(view)}
        disabled={readOnly}
        className="min-w-0 flex-1 text-left"
      >
        <span className="text-sm font-medium">{view.name}</span>
        <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
          <code>{view.slug}</code>
        </span>
      </button>
      {/* Badge typu tylko w grupie „Listy i widoki" (rozróżnia lista/kalendarz/kanban/dashboard).
          W grupie „Formularze" byłby redundantny. */}
      {showType && <Badge color="gray">{t(`moduleTemplateEditor.viewType.${view.type}`)}</Badge>}
      {view.isDefault && <Badge color="green">{t('moduleTemplateEditor.defaultView')}</Badge>}
      {/* Hub Krok 2 — projektowanie layoutu widoku (formularz / Kreator Listy).
          Zablokowane przy niezapisanej strukturze: projektant pracuje na zapisanej wersji.
          Ukryte w trybie read-only (Krok 5). */}
      {!readOnly && (view.type === 'form' || view.type === 'list') && (
        <button
          type="button"
          disabled={structureDirty}
          onClick={() =>
            navigate(
              `/module-templates/${templateSlug}/entities/${entity.slug}/views/${view.slug}/${
                view.type === 'form' ? 'form-builder' : 'list-builder'
              }`,
            )
          }
          title={
            structureDirty
              ? t('moduleTemplateEditor.layoutDesigner.dirtyHint')
              : view.type === 'form'
                ? t('moduleTemplateEditor.layoutDesigner.designForm')
                : t('moduleTemplateEditor.layoutDesigner.designList')
          }
          className="inline-flex items-center gap-1 rounded border border-gray-200 px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          data-testid={`design-view-${view.slug}`}
        >
          <Paintbrush className="h-3 w-3" />
          {view.type === 'form'
            ? t('moduleTemplateEditor.layoutDesigner.designFormButton')
            : t('moduleTemplateEditor.layoutDesigner.designListButton')}
        </button>
      )}
      {/* Podgląd na żywo (makieta) — read-only, syntetyczne dane, bez sieci.
          Dostępny też w trybie read-only (Krok 5): nic nie modyfikuje. */}
      {(view.type === 'form' || view.type === 'list') && (
        <button
          type="button"
          onClick={() => setPreviewView(view)}
          title={t('moduleTemplateEditor.preview.button')}
          className="inline-flex items-center gap-1 rounded border border-gray-200 px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          data-testid={`view-preview-${view.slug}`}
        >
          <Eye className="h-3 w-3" />
          {t('moduleTemplateEditor.preview.button')}
        </button>
      )}
      {!readOnly && (
        <button
          type="button"
          onClick={() => onChange(views.filter((v) => v.slug !== view.slug))}
          className="rounded p-1 text-gray-400 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 group-hover:opacity-100 dark:hover:bg-red-950/40"
          aria-label={t('common.delete')}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </li>
  );

  const renderGroup = (
    titleKey: string,
    emptyKey: string,
    groupViews: ModuleTemplateViewDefinition[],
    addType: ModuleTemplateViewType,
    addKey: string,
    showType: boolean,
  ) => (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {t(titleKey)} ({groupViews.length})
        </h4>
        {!readOnly && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setCreateType(addType)}
            disabled={atLimit}
            title={atLimit ? t('moduleTemplateEditor.viewLimit') : undefined}
          >
            <Plus className="mr-1 h-4 w-4" />
            {t(addKey)}
          </Button>
        )}
      </div>
      {groupViews.length === 0 ? (
        <p className="rounded border border-dashed border-gray-300 p-3 text-center text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
          {t(emptyKey)}
        </p>
      ) : (
        <ul className="flex flex-col gap-1">{groupViews.map((v) => renderView(v, showType))}</ul>
      )}
    </div>
  );

  const isForm = kind === 'form';

  // Bramka projektowania: przycisk „Projektuj" jest wyłączony, dopóki struktura ma
  // niezapisane zmiany (projektant pracuje na zapisanej wersji). Inline-komunikat,
  // bo sam wyszarzony przycisk + tooltip bywa przeoczony („nie da się projektować").
  const showDirtyHint =
    !readOnly && structureDirty && views.some((v) => v.type === 'form' || v.type === 'list');

  return (
    <div>
      {showDirtyHint && (
        <div
          className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
          data-testid="views-save-structure-hint"
        >
          {t('moduleTemplateEditor.layoutDesigner.dirtyHint')}
        </div>
      )}
      {isForm
        ? renderGroup(
            'moduleTemplateEditor.viewGroups.forms',
            'moduleTemplateEditor.viewGroups.emptyForms',
            formViews,
            'form',
            'moduleTemplateEditor.viewGroups.addForm',
            false,
          )
        : renderGroup(
            'moduleTemplateEditor.viewGroups.lists',
            'moduleTemplateEditor.viewGroups.emptyLists',
            collectionViews,
            'list',
            'moduleTemplateEditor.viewGroups.addList',
            true,
          )}

      <ViewFormModal
        open={createType !== null}
        mode="create"
        initialType={createType ?? 'list'}
        fields={entity.fields}
        existingSlugs={existingSlugs}
        onClose={() => setCreateType(null)}
        onSubmit={(view) => {
          onChange([...views, view]);
          setCreateType(null);
        }}
      />

      {editView && (
        <ViewFormModal
          open
          mode="edit"
          view={editView}
          fields={entity.fields}
          existingSlugs={existingSlugs.filter((s) => s !== editView.slug)}
          onClose={() => setEditView(null)}
          onSubmit={(view) => {
            onChange(views.map((v) => (v.slug === editView.slug ? view : v)));
            setEditView(null);
          }}
        />
      )}

      {previewView && (
        <EntityViewPreview
          open
          view={previewView}
          fields={entity.fields}
          onClose={() => setPreviewView(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal — encja (metadane)
// ---------------------------------------------------------------------------

// Lista gotowych kartotek systemowych jest pobierana dynamicznie w EntityFormModal
// (`GET /api/admin/kartoteki`) — patrz `systemKartotekiQuery`. Slugi odpowiadają top-level
// `system_modules` (installer mapuje referencję na istniejącą tabelę tenanta). Dokumenty
// (division='klasy') świadomie pominięte — podpinają się innym mechanizmem (F20, domena Alexa).

function EntityFormModal({
  open,
  mode,
  entity,
  templateSlug,
  existingSlugs,
  onClose,
  onSubmit,
}: {
  open: boolean;
  mode: 'create' | 'edit';
  entity?: ModuleTemplateEntityDefinition;
  /** Kod bieżącego szablonu — wykluczany z pickera odwołań do kartotek Kreatora (Etap 3). */
  templateSlug: string;
  existingSlugs: string[];
  onClose: () => void;
  onSubmit: (entities: ModuleTemplateEntityDefinition[]) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  // Tryb create: zaznacz dowolnie wiele gotowych kartotek systemowych (checkboxy)
  // ALBO utwórz jedną nową (pola niżej) — wszystko na jednym ekranie. Zaznaczenie
  // którejkolwiek gotowej wygrywa (pola „nowej" wyłączone) — moduł może mieć wiele kartotek.
  const [systemSlugs, setSystemSlugs] = useState<string[]>([]);
  // Etap 3: zaznaczone kartoteki Kreatora (odwołania cross-szablonowe). Klucz = `${kod}::${encja}`.
  const [creatorKeys, setCreatorKeys] = useState<string[]>([]);
  // Kartoteka na bazie klasy dokumentu (document_class): wybrana klasa + systemowy typ.
  const [docClass, setDocClass] = useState<DocumentClass | ''>('');
  const [docTypeId, setDocTypeId] = useState('');

  useEffect(() => {
    if (open) {
      setName(entity?.name ?? '');
      setSlug(entity?.slug ?? '');
      setSlugTouched(mode === 'edit');
      setSystemSlugs([]);
      setCreatorKeys([]);
      setDocClass('');
      setDocTypeId('');
    }
  }, [open, entity, mode]);

  // Picker gotowych kartotek systemowych — PEŁNA lista z rejestru (`GET /api/admin/kartoteki`),
  // nie zahardkodowane 4. Dzięki temu można podpiąć każdą kartotekę systemową (Kontrahenci,
  // Firmy, Pracownicy, Użytkownicy, Towary, Kontakty…), a nowe dorzucają się same.
  // Klasy dokumentów (Faktury zakupu/sprzedaży, Umowy, Pisma, Handlowe, Wewnętrzne) też
  // dodane do wyboru (decyzja Eugenii 2026-06-25) — spłaszczone z wpisu „Dokumenty" (division='klasy').
  // UWAGA: runtime referencji daje generyczny ekran kartoteki, nie dedykowany ekran dokumentu
  // (obszar Alexa F20) — do sanity-checku przy realnej instalacji.
  const systemKartotekiQuery = useQuery({
    queryKey: ['admin', 'kartoteki'],
    queryFn: async () =>
      (
        await api.get<{
          kartoteki: {
            name: string;
            slug?: string;
            source: string;
            division: string;
            classes?: { name: string; slug: string }[];
            editTarget?: { templateCode: string; entitySlug: string } | null;
            published?: boolean;
          }[];
        }>('/admin/kartoteki')
      ).data,
    enabled: open && mode === 'create',
    retry: false,
  });
  const allKartoteki = systemKartotekiQuery.data?.kartoteki ?? [];
  // Top-level kartoteki systemowe (nie-dokumentowe) + klasy dokumentów (z wpisu „Dokumenty").
  const systemKartoteki = [
    ...allKartoteki
      .filter((k) => k.source === 'systemowa' && k.division !== 'klasy' && !!k.slug)
      .map((k) => ({ slug: k.slug as string, name: k.name })),
    ...allKartoteki
      .filter((k) => k.source === 'systemowa' && k.division === 'klasy')
      .flatMap((k) => k.classes ?? [])
      .map((c) => ({ slug: c.slug, name: c.name })),
  ];

  // Etap 3 — kartoteki Kreatora dostępne jako odwołanie cross-szablonowe: tylko OPUBLIKOWANE
  // szablony (instalować można tylko opublikowane) i NIE ten sam szablon (self-reference idzie
  // odwołaniem w obrębie szablonu). Klucz = `${templateCode}::${entitySlug}`.
  const creatorKartoteki = allKartoteki
    .filter(
      (k) =>
        k.source === 'kreator' &&
        k.published === true &&
        k.editTarget &&
        k.editTarget.templateCode !== templateSlug,
    )
    .map((k) => ({
      key: `${k.editTarget!.templateCode}::${k.editTarget!.entitySlug}`,
      templateCode: k.editTarget!.templateCode,
      entitySlug: k.editTarget!.entitySlug,
      name: k.name,
    }));

  // Systemowe (globalne) typy wybranej klasy — tylko opublikowane. Enabled dopiero po
  // wyborze klasy (bez klasy nie ma o co pytać).
  const globalTypesQuery = useQuery({
    queryKey: ['admin', 'global-document-types', docClass, 'published'],
    queryFn: async () =>
      (
        await api.get<{ types: AdminGlobalDocumentType[] }>(
          `/admin/global-document-types?class=${docClass}&status=published`,
        )
      ).data,
    enabled: open && mode === 'create' && docClass !== '',
    retry: false,
  });
  const globalTypes = globalTypesQuery.data?.types ?? [];

  const isSystemPick = mode === 'create' && (systemSlugs.length > 0 || creatorKeys.length > 0);
  // Tryb „na bazie klasy dokumentu" (create) — aktywny gdy wybrano klasę.
  const isDocumentClassMode = mode === 'create' && docClass !== '';
  // Edycja encji-odwołania — nazwa i slug są stałe (wynikają z kartoteki).
  const isReferenceEdit =
    mode === 'edit' &&
    (entity?.ddlStrategy === 'reference_to_system_entity' ||
      entity?.ddlStrategy === 'reference_to_creator_entity' ||
      entity?.ddlStrategy === 'document_class');

  const effectiveSlug = slugTouched ? slug : slugify(name);
  const slugValid = isValidSlug(effectiveSlug);
  const slugUnique = !existingSlugs.includes(effectiveSlug);
  const baseFieldsValid = Boolean(name) && slugValid && slugUnique;
  // Systemowy typ (docTypeId) jest OPCJONALNY — element klasy dokumentu może być kotwicą
  // całej klasy; zawężenie do typu(-ów) dokumentu robi się na pozycji menu, nie tutaj.
  const canSubmit = isSystemPick ? true : baseFieldsValid;

  const toggleSystem = (slug: string) =>
    setSystemSlugs((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );
  const toggleCreator = (key: string) =>
    setCreatorKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  const changeDocClass = (value: string) => {
    setDocClass((value as DocumentClass) || '');
    setDocTypeId(''); // reset typu przy zmianie klasy (typy są per-klasa)
  };

  const submit = () => {
    if (!canSubmit) return;
    if (isSystemPick) {
      // Każda zaznaczona gotowa kartoteka → osobna encja-odwołanie (instalator podepnie
      // istniejącą tabelę klienta). Zasiew domyślnych widoków w szkicu (M3b):
      //  - widok 'form' (pusty formConfig — układ projektowany w kreatorze zakładki Formularz)
      //  - widok 'list' (minimalny listConfig z kolumną record_name — wymagana min-1-kolumna)
      const base = entity?.sortOrder ?? 0;
      const refs: ModuleTemplateEntityDefinition[] = systemSlugs.map((s, i) => {
        const sk = systemKartoteki.find((k) => k.slug === s);
        return {
          slug: s,
          name: sk ? sk.name : s,
          sortOrder: base + i,
          navigationMode: 'tab',
          ddlStrategy: 'reference_to_system_entity',
          referenceSystemModuleSlug: s,
          icon: undefined,
          fields: [],
          views: [
            {
              slug: 'form',
              name: t('referenceFormBuilder.defaultViewName'),
              type: 'form' as const,
              isDefault: true,
              sortOrder: 0,
            },
            {
              slug: 'list',
              name: t('referenceFormBuilder.defaultListViewName'),
              type: 'list' as const,
              isDefault: true,
              sortOrder: 1,
              listConfig: {
                ...emptyListConfig,
                columns: [{ id: 'record_name', visible: true, width: 200, order: 0 }],
              } satisfies ListConfig,
            },
          ],
        };
      });

      // Etap 3 — kartoteki Kreatora (odwołania cross-szablonowe). Slug encji-odwołania =
      // slug encji źródła (≤20 znaków, unikalny w szablonie). Bez własnych pól/widoków —
      // układ pochodzi z modułu źródła u klienta (MVP: brak per-modułowego buildera).
      // Dedup po slugu (gdy dwa źródła mają encję o tym samym slugu — bierzemy pierwszą;
      // duplikat i tak odrzuci walidacja struktury) i pomijamy slugi już obecne w szablonie.
      const seenCreatorSlugs = new Set(refs.map((r) => r.slug));
      const creatorRefs: ModuleTemplateEntityDefinition[] = [];
      creatorKeys.forEach((key) => {
        const ck = creatorKartoteki.find((k) => k.key === key);
        if (!ck) return;
        const refSlug = ck.entitySlug;
        if (seenCreatorSlugs.has(refSlug) || existingSlugs.includes(refSlug)) return;
        seenCreatorSlugs.add(refSlug);
        creatorRefs.push({
          slug: refSlug,
          name: ck.name,
          sortOrder: base + refs.length + creatorRefs.length,
          navigationMode: 'tab',
          ddlStrategy: 'reference_to_creator_entity',
          referenceTemplateCode: ck.templateCode,
          referenceTargetEntitySlug: ck.entitySlug,
          icon: undefined,
          fields: [],
          views: [],
        });
      });

      onSubmit([...refs, ...creatorRefs]);
      return;
    }
    // Na bazie klasy dokumentu (create) — rekordy trafiają do wspólnej tabeli `documents`
    // (bez nowej tabeli), zawężone do wybranej klasy i systemowego typu.
    if (isDocumentClassMode) {
      onSubmit([
        {
          slug: effectiveSlug,
          name,
          sortOrder: entity?.sortOrder ?? 0,
          navigationMode: 'tab',
          ddlStrategy: 'document_class',
          documentClass: docClass,
          documentGlobalTypeId: docTypeId || undefined,
          icon: undefined,
          fields: [],
          views: [],
        },
      ]);
      return;
    }
    onSubmit([
      {
        slug: effectiveSlug,
        name,
        sortOrder: entity?.sortOrder ?? 0,
        navigationMode: entity?.navigationMode ?? 'tab',
        ddlStrategy: entity?.ddlStrategy ?? 'new_table',
        referenceSystemModuleSlug: entity?.referenceSystemModuleSlug,
        // Etap 3 — zachowaj cel odwołania Kreatora przy edycji (slug/nazwa zablokowane).
        referenceTemplateCode: entity?.referenceTemplateCode,
        referenceTargetEntitySlug: entity?.referenceTargetEntitySlug,
        // Zachowaj wiązanie klasy dokumentu przy edycji (slug/nazwa zablokowane).
        documentClass: entity?.documentClass,
        documentGlobalTypeId: entity?.documentGlobalTypeId,
        icon: entity?.icon,
        fields: entity?.fields ?? [],
        views: entity?.views ?? [],
      },
    ]);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      // Picker bywa długi (wiele kartotek systemowych + klas dokumentów + z Kreatora) —
      // szerszy modal + checkboxy w kolumnach. (Docelowo dropdown — TODO gdy listy urosną.)
      size="xl"
      title={
        mode === 'create'
          ? t('moduleTemplateEditor.newEntity')
          : t('moduleTemplateEditor.editEntity')
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {mode === 'create' ? t('common.create') : t('common.save')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        {/* Tryb create: gotowa kartoteka (dropdown) ORAZ nowa (pola niżej) na jednym
            ekranie. Wybór gotowej wyłącza pola nowej. */}
        {mode === 'create' && (
          <>
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-text-secondary">
                {t('moduleTemplateEditor.systemPick.pickLabel')}
              </span>
              <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
                {systemKartoteki.map((k) => {
                  const already = existingSlugs.includes(k.slug);
                  return (
                    <Checkbox
                      key={k.slug}
                      label={
                        already
                          ? `${k.name} — ${t('moduleTemplateEditor.systemPick.alreadyAdded')}`
                          : k.name
                      }
                      checked={systemSlugs.includes(k.slug)}
                      disabled={already || isDocumentClassMode}
                      onChange={() => toggleSystem(k.slug)}
                    />
                  );
                })}
              </div>
              {systemKartotekiQuery.isLoading && (
                <span className="text-xs text-text-muted">{t('common.loading')}</span>
              )}
              <span className="text-xs text-text-muted">
                {t('moduleTemplateEditor.systemPick.hint')}
              </span>
            </div>

            {/* Etap 3 — kartoteki z Kreatora (odwołania cross-szablonowe). Tylko opublikowane
                szablony, bez bieżącego. Pokazujemy sekcję tylko gdy są jakieś do wyboru. */}
            {creatorKartoteki.length > 0 && (
              <div className="flex flex-col gap-2" data-testid="creator-kartoteki-pick">
                <span className="text-sm font-medium text-text-secondary">
                  {t('moduleTemplateEditor.creatorPick.pickLabel')}
                </span>
                <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
                  {creatorKartoteki.map((k) => {
                    const already = existingSlugs.includes(k.entitySlug);
                    return (
                      <Checkbox
                        key={k.key}
                        label={
                          already
                            ? `${k.name} (${k.templateCode}) — ${t('moduleTemplateEditor.systemPick.alreadyAdded')}`
                            : `${k.name} (${k.templateCode})`
                        }
                        checked={creatorKeys.includes(k.key)}
                        disabled={already || isDocumentClassMode}
                        onChange={() => toggleCreator(k.key)}
                      />
                    );
                  })}
                </div>
                <span className="text-xs text-text-muted">
                  {t('moduleTemplateEditor.creatorPick.hint')}
                </span>
              </div>
            )}

            {/* Na bazie klasy dokumentu (document_class) — klasa + systemowy typ */}
            <div className="flex flex-col gap-2" data-testid="document-class-pick">
              <span className="text-sm font-medium text-text-secondary">
                {t('moduleTemplateEditor.documentClassPick.pickLabel')}
              </span>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <FormField label={t('moduleTemplateEditor.documentClassPick.classLabel')}>
                  <Dropdown
                    placeholder={t('moduleTemplateEditor.documentClassPick.classPlaceholder')}
                    value={docClass || undefined}
                    disabled={isSystemPick}
                    onChange={(v) => changeDocClass(v)}
                    options={DOCUMENT_CLASS_VALUES.map((c) => ({
                      value: c,
                      label: t(`adminDocumentTypes.classes.${c}`),
                    }))}
                  />
                </FormField>
                {isDocumentClassMode && (
                  <FormField label={t('moduleTemplateEditor.documentClassPick.typeLabel')}>
                    <Dropdown
                      placeholder={t('moduleTemplateEditor.documentClassPick.typePlaceholder')}
                      value={docTypeId || undefined}
                      onChange={(v) => setDocTypeId(v)}
                      options={globalTypes.map((gt) => ({ value: gt.id, label: gt.name }))}
                    />
                  </FormField>
                )}
              </div>
              {isDocumentClassMode && globalTypesQuery.isLoading && (
                <span className="text-xs text-text-muted">{t('common.loading')}</span>
              )}
              {isDocumentClassMode && !globalTypesQuery.isLoading && globalTypes.length === 0 && (
                <span className="text-xs text-amber-600">
                  {t('moduleTemplateEditor.documentClassPick.typeEmpty')}
                </span>
              )}
              <span className="text-xs text-text-muted">
                {t('moduleTemplateEditor.documentClassPick.hint')}
              </span>
            </div>

            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-text-muted">
              <span className="h-px flex-1 bg-border-primary" />
              {t('moduleTemplateEditor.systemPick.orNew')}
              <span className="h-px flex-1 bg-border-primary" />
            </div>
          </>
        )}

        <Input
          label={t('moduleTemplateEditor.field.name')}
          value={isSystemPick ? '' : name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('moduleTemplateEditor.field.entityNamePlaceholder')}
          disabled={isSystemPick || isReferenceEdit}
          hint={
            isReferenceEdit ? t('moduleTemplateEditor.field.systemEntityNameLocked') : undefined
          }
        />
        <Input
          label={t('moduleTemplateEditor.field.slug')}
          hint={
            isReferenceEdit
              ? t('moduleTemplateEditor.field.systemEntityNameLocked')
              : t('moduleTemplateEditor.field.slugHint')
          }
          value={isSystemPick ? '' : effectiveSlug}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(e.target.value);
          }}
          error={
            !isSystemPick && !isReferenceEdit && effectiveSlug && !slugValid
              ? t('moduleTemplateEditor.field.slugInvalid')
              : !isSystemPick && !isReferenceEdit && effectiveSlug && !slugUnique
                ? t('moduleTemplateEditor.field.slugTaken')
                : undefined
          }
          placeholder="np. wnioski"
          disabled={isSystemPick || isReferenceEdit}
        />
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Modal — pole
// ---------------------------------------------------------------------------

function FieldFormModal({
  open,
  mode,
  field,
  existingSlugs,
  siblingFields,
  referenceTargets,
  onClose,
  onSubmit,
}: {
  open: boolean;
  mode: 'create' | 'edit';
  field?: ModuleTemplateFieldDefinition;
  existingSlugs: string[];
  /** Pola-sąsiedzi tej samej encji (bez edytowanego pola samego siebie) — kandydaci
   *  do wiązania parametrów `record_field` (Etap 10.5). */
  siblingFields: ModuleTemplateFieldDefinition[];
  referenceTargets: string[];
  onClose: () => void;
  onSubmit: (field: ModuleTemplateFieldDefinition) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [type, setType] = useState<ModuleTemplateFieldType>('text');
  const [isRequired, setIsRequired] = useState(false);
  const [description, setDescription] = useState('');
  const [options, setOptions] = useState<ModuleTemplateFieldOption[]>([]);
  const [dictionaryCode, setDictionaryCode] = useState<string | null>(null);
  const [dictionaryDisplayMode, setDictionaryDisplayMode] = useState<
    'label' | 'code' | 'code_label' | null
  >(null);
  const [dictionaryParamBindings, setDictionaryParamBindings] =
    useState<DictionaryParamBindingsMap | null>(null);
  const [referenceTargetSlug, setReferenceTargetSlug] = useState('');

  useEffect(() => {
    if (open) {
      setName(field?.name ?? '');
      setSlug(field?.slug ?? '');
      setSlugTouched(mode === 'edit');
      setType(field?.type ?? 'text');
      setIsRequired(field?.isRequired ?? false);
      setDescription(field?.description ?? '');
      setOptions(field?.options ?? []);
      setDictionaryCode(field?.dictionaryCode ?? null);
      setDictionaryDisplayMode(field?.dictionaryDisplayMode ?? null);
      setDictionaryParamBindings(field?.dictionaryParamBindings ?? null);
      setReferenceTargetSlug(field?.referenceTargetSlug ?? '');
    }
  }, [open, field, mode]);

  // Panel ITE widzi tylko katalog globalny — deklaracje parametrów pochodzą z rejestru
  // źródeł (`useDictionarySources`), dopasowanego po `sourceKey` wybranego słownika
  // (`useGlobalDictionaries`), a dla źródeł SQL z `sourceParams` samego słownika.
  const { data: globalDictionaries } = useGlobalDictionaries(false);
  const { data: dictionarySources } = useDictionarySources();
  const selectedDict = dictionaryCode
    ? globalDictionaries?.find((d) => d.code === dictionaryCode)
    : undefined;
  const selectedSource =
    selectedDict?.sourceKind === 'external' && selectedDict.sourceKey
      ? dictionarySources?.find((s) => s.key === selectedDict.sourceKey)
      : undefined;
  const declaredParams = selectedSource?.params ?? selectedDict?.sourceParams ?? [];
  const siblingFieldOptions = siblingFields.map((f) => ({ slug: f.slug, name: f.name }));

  const effectiveSlug = slugTouched ? slug : slugify(name);
  const slugValid = isValidSlug(effectiveSlug);
  const slugUnique = !existingSlugs.includes(effectiveSlug);
  const needsOptions = type === 'select' || type === 'multi_select';
  const needsReference = type === 'reference';
  // Pole ze słownikiem globalnym nie wymaga opcji wpisanych ręcznie (opcje w runtime
  // pochodzą ze słownika) — lustro walidatora backendu (`field.select_without_options`).
  const optionsValid = !needsOptions || options.length > 0 || Boolean(dictionaryCode);
  const referenceValid = !needsReference || Boolean(referenceTargetSlug);
  const canSubmit = Boolean(name) && slugValid && slugUnique && optionsValid && referenceValid;

  const typeOptions = MODULE_TEMPLATE_FIELD_TYPES.map((ft) => ({
    value: ft,
    label: t(`moduleTemplateEditor.fieldType.${ft}`),
  }));

  const submit = () => {
    if (!canSubmit) return;
    // Pola encji szablonu modułu nie mają rekordu-rodzica, więc wiązanie `parent_field` jest tu
    // stanem niemożliwym (edytor go nie emituje; mogłoby przyjść tylko ze zniekształconych
    // danych). Guard rzuca — łapiemy w handlerze kliku i pokazujemy błąd, nie zapisujemy pola.
    let bindings: ReturnType<typeof assertNoParentFieldBindings> | undefined;
    try {
      bindings =
        needsOptions && dictionaryCode
          ? assertNoParentFieldBindings(dictionaryParamBindings)
          : undefined;
    } catch {
      toast.show({
        variant: 'error',
        title: t('moduleTemplateEditor.field.parentBindingNotAllowed'),
      });
      return;
    }
    onSubmit({
      slug: effectiveSlug,
      name,
      type,
      isRequired,
      description: description || undefined,
      options: needsOptions && !dictionaryCode ? options : undefined,
      dictionaryCode: needsOptions ? dictionaryCode : undefined,
      dictionaryDisplayMode: needsOptions ? dictionaryDisplayMode : undefined,
      dictionaryParamBindings: bindings,
      referenceTargetSlug: needsReference ? referenceTargetSlug : undefined,
      sortOrder: field?.sortOrder ?? 0,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        mode === 'create' ? t('moduleTemplateEditor.newField') : t('moduleTemplateEditor.editField')
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {mode === 'create' ? t('common.create') : t('common.save')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <Input
          label={t('moduleTemplateEditor.field.name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('moduleTemplateEditor.field.fieldNamePlaceholder')}
        />
        <Input
          label={t('moduleTemplateEditor.field.slug')}
          hint={t('moduleTemplateEditor.field.slugHint')}
          value={effectiveSlug}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(e.target.value);
          }}
          error={
            effectiveSlug && !slugValid
              ? t('moduleTemplateEditor.field.slugInvalid')
              : effectiveSlug && !slugUnique
                ? t('moduleTemplateEditor.field.slugTaken')
                : undefined
          }
        />
        <FormField label={t('moduleTemplateEditor.field.type')}>
          <Dropdown
            options={typeOptions}
            value={type}
            onChange={(v) => setType(v as ModuleTemplateFieldType)}
          />
        </FormField>
        {needsOptions && (
          <>
            <GlobalDictionaryPicker
              value={dictionaryCode}
              onChange={(code) => {
                setDictionaryCode(code);
                setDictionaryParamBindings(null);
              }}
              data-testid="field-dictionary-picker"
            />
            {dictionaryCode && (
              <FormField label={t('common.dictionaryDisplayModeOverride.label')}>
                <Dropdown
                  value={dictionaryDisplayMode ?? ''}
                  onChange={(v) =>
                    setDictionaryDisplayMode((v || null) as 'label' | 'code' | 'code_label' | null)
                  }
                  options={[
                    {
                      value: '',
                      label: t('common.dictionaryDisplayModeOverride.useDictionaryDefault'),
                    },
                    {
                      value: 'label',
                      label: t('common.dictionaryDisplayModeOverride.options.label'),
                    },
                    {
                      value: 'code',
                      label: t('common.dictionaryDisplayModeOverride.options.code'),
                    },
                    {
                      value: 'code_label',
                      label: t('common.dictionaryDisplayModeOverride.options.code_label'),
                    },
                  ]}
                  data-testid="field-dictionary-display-mode"
                />
              </FormField>
            )}
            {dictionaryCode && (
              <DictionaryParamBindingsEditor
                params={declaredParams}
                siblingFields={siblingFieldOptions}
                value={dictionaryParamBindings}
                onChange={setDictionaryParamBindings}
                data-testid="field-dictionary-param-bindings"
              />
            )}
            {dictionaryCode ? (
              <p className="text-xs text-text-muted" data-testid="field-dictionary-options-hint">
                {t('moduleTemplateEditor.field.optionsFromDictionaryHint')}
              </p>
            ) : (
              <OptionsEditor options={options} onChange={setOptions} />
            )}
          </>
        )}
        {needsReference && (
          <Input
            label={t('moduleTemplateEditor.field.referenceTargetSlug')}
            hint={t('moduleTemplateEditor.field.referenceTargetSlugHint')}
            value={referenceTargetSlug}
            onChange={(e) => setReferenceTargetSlug(e.target.value)}
            placeholder={referenceTargets[0] ?? 'np. employees'}
          />
        )}
        <Textarea
          label={t('moduleTemplateEditor.field.description')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
        />
        <Checkbox
          label={t('moduleTemplateEditor.field.required')}
          checked={isRequired}
          onChange={(e) => setIsRequired(e.target.checked)}
        />
      </div>
    </Modal>
  );
}

/** Lekki edytor opcji słownikowych (local state — zapis razem ze strukturą). */
function OptionsEditor({
  options,
  onChange,
}: {
  options: ModuleTemplateFieldOption[];
  onChange: (options: ModuleTemplateFieldOption[]) => void;
}) {
  const { t } = useTranslation();
  const [newLabel, setNewLabel] = useState('');

  const addOption = () => {
    const label = newLabel.trim();
    if (!label) return;
    let value = slugify(label) || 'opt';
    let suffix = 2;
    while (options.some((o) => o.value === value)) {
      value = `${slugify(label) || 'opt'}_${suffix++}`;
    }
    onChange([...options, { value, label }]);
    setNewLabel('');
  };

  return (
    <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
      <p className="mb-2 text-xs font-medium text-gray-500 dark:text-gray-400">
        {t('moduleTemplateEditor.field.options')}
      </p>
      {options.length === 0 ? (
        <p className="mb-2 text-xs text-gray-400">{t('moduleTemplateEditor.field.optionsEmpty')}</p>
      ) : (
        <ul className="mb-2 flex flex-col gap-1">
          {options.map((opt, idx) => (
            <li key={opt.value} className="flex items-center gap-2 text-sm">
              <span className="flex-1 truncate">{opt.label}</span>
              <code className="text-xs text-gray-400">{opt.value}</code>
              <button
                type="button"
                onClick={() => onChange(options.filter((_, i) => i !== idx))}
                className="text-gray-400 hover:text-red-600"
                aria-label={t('common.delete')}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-2">
        <Input
          containerClassName="flex-1"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addOption();
            }
          }}
          placeholder={t('moduleTemplateEditor.field.optionPlaceholder')}
        />
        <Button size="sm" variant="secondary" onClick={addOption} disabled={!newLabel.trim()}>
          {t('moduleTemplateEditor.field.addOption')}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal — widok
// ---------------------------------------------------------------------------

function ViewFormModal({
  open,
  mode,
  view,
  initialType = 'list',
  fields,
  existingSlugs,
  onClose,
  onSubmit,
}: {
  open: boolean;
  mode: 'create' | 'edit';
  view?: ModuleTemplateViewDefinition;
  /** Typ startowy w trybie create (grupa „Formularze" → 'form', „Listy" → 'list'). */
  initialType?: ModuleTemplateViewType;
  fields: ModuleTemplateFieldDefinition[];
  existingSlugs: string[];
  onClose: () => void;
  onSubmit: (view: ModuleTemplateViewDefinition) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [type, setType] = useState<ModuleTemplateViewType>('list');
  const [isDefault, setIsDefault] = useState(false);
  const [columnSlugs, setColumnSlugs] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      setName(view?.name ?? '');
      setSlug(view?.slug ?? '');
      setSlugTouched(mode === 'edit');
      setType(view?.type ?? initialType);
      setIsDefault(view?.isDefault ?? false);
      // Checkboxy pokazują tylko pola encji — wbudowane kolumny (record_name/record_number)
      // i reszta configu (sort, filtry, widgety z huba) są zachowywane przy zapisie.
      const fieldSlugSet = new Set(fields.map((f) => f.slug));
      setColumnSlugs(
        view?.listConfig?.columns.filter((c) => fieldSlugSet.has(c.id)).map((c) => c.id) ??
          fields.map((f) => f.slug),
      );
    }
  }, [open, view, mode, fields, initialType]);

  const effectiveSlug = slugTouched ? slug : slugify(name);
  const slugValid = isValidSlug(effectiveSlug);
  const slugUnique = !existingSlugs.includes(effectiveSlug);
  const isList = type === 'list';
  const columnsValid = !isList || columnSlugs.length > 0;
  const canSubmit = Boolean(name) && slugValid && slugUnique && columnsValid;

  const typeOptions = MODULE_TEMPLATE_VIEW_TYPES.map((vt) => ({
    value: vt,
    label: t(`moduleTemplateEditor.viewType.${vt}`),
  }));

  const toggleColumn = (fieldSlug: string) => {
    setColumnSlugs((prev) =>
      prev.includes(fieldSlug) ? prev.filter((s) => s !== fieldSlug) : [...prev, fieldSlug],
    );
  };

  const submit = () => {
    if (!canSubmit) return;
    let listConfig: ListConfig | undefined;
    if (isList) {
      const pickedSlugs = fields.filter((f) => columnSlugs.includes(f.slug)).map((f) => f.slug);
      const base = view?.type === 'list' ? view.listConfig : undefined;
      if (base) {
        // Edycja: dociągamy/odpinamy kolumny pól, reszta configu (wbudowane kolumny,
        // sort, filtry, widgety zaprojektowane w hubie) zostaje nietknięta.
        const fieldSlugSet = new Set(fields.map((f) => f.slug));
        const kept = base.columns.filter(
          (c) => !fieldSlugSet.has(c.id) || pickedSlugs.includes(c.id),
        );
        const added = pickedSlugs
          .filter((s) => !base.columns.some((c) => c.id === s))
          .map((s) => ({ id: s, visible: true, width: 'auto' as const, order: 0 }));
        const columns = [...kept, ...added].map((c, i) => ({ ...c, order: i }));
        listConfig = {
          ...base,
          columns,
          defaultSort:
            base.defaultSort && columns.some((c) => c.id === base.defaultSort?.column)
              ? base.defaultSort
              : null,
        };
      } else {
        listConfig = templateListConfigFromLegacy({
          columns: pickedSlugs.map((s, i) => ({ fieldSlug: s, visible: true, sortOrder: i })),
        });
      }
    }
    onSubmit({
      slug: effectiveSlug,
      name,
      type,
      isDefault,
      sortOrder: view?.sortOrder ?? 0,
      listConfig,
      // Układ formularza projektuje hub (Krok 2) — modal go tylko przenosi dalej,
      // i tylko gdy typ widoku nadal pasuje (Zod odrzuca config niezgodny z typem).
      formConfig: type === 'form' ? view?.formConfig : undefined,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        mode === 'create' ? t('moduleTemplateEditor.newView') : t('moduleTemplateEditor.editView')
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {mode === 'create' ? t('common.create') : t('common.save')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <Input
          label={t('moduleTemplateEditor.field.name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('moduleTemplateEditor.field.viewNamePlaceholder')}
        />
        <Input
          label={t('moduleTemplateEditor.field.slug')}
          hint={t('moduleTemplateEditor.field.slugHint')}
          value={effectiveSlug}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(e.target.value);
          }}
          error={
            effectiveSlug && !slugValid
              ? t('moduleTemplateEditor.field.slugInvalid')
              : effectiveSlug && !slugUnique
                ? t('moduleTemplateEditor.field.slugTaken')
                : undefined
          }
        />
        <FormField label={t('moduleTemplateEditor.field.viewType')}>
          <Dropdown
            options={typeOptions}
            value={type}
            onChange={(v) => setType(v as ModuleTemplateViewType)}
          />
        </FormField>
        {isList && (
          <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
            <p className="mb-2 text-xs font-medium text-gray-500 dark:text-gray-400">
              {t('moduleTemplateEditor.field.columns')}
            </p>
            {fields.length === 0 ? (
              <p className="text-xs text-gray-400">
                {t('moduleTemplateEditor.field.columnsNoFields')}
              </p>
            ) : (
              <div className="flex flex-col gap-1.5">
                {fields.map((f) => (
                  <Checkbox
                    key={f.slug}
                    label={f.name}
                    checked={columnSlugs.includes(f.slug)}
                    onChange={() => toggleColumn(f.slug)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        <Checkbox
          label={t('moduleTemplateEditor.field.isDefault')}
          checked={isDefault}
          onChange={(e) => setIsDefault(e.target.checked)}
        />
      </div>
    </Modal>
  );
}
